import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronRightIcon,
  FilterFunnelIcon,
  LoaderIcon,
  NotesHintIcon,
  TrashIcon,
} from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { countPdfPages } from '@/utils/pdfPageCount';
import { previewRagUploadFileFirstPage, type RagFilePreviewResult } from '@/utils/ragFilePreview';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeishuAuthStore } from '@/store/feishu-auth.store';
import { useAuthStore } from '@/store/auth.store';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ragApi,
  normalizeStreamLogText,
  redactDocIdsInLogDisplay,
  ragIngestRowToResult,
  type RagJobStatusResponse,
  type RagPdfJobResult,
  type RagCollectionRow,
  type RagRerunJob,
} from '@/api/rag.api';
import { apiClient } from '@/api/request';
import { ApiError } from '@/api/errors';
import { FeishuRAGPermissionRecommendations } from '@/components/FeishuRAGPermissionRecommendations';

/** Align with RAG ingest POST /run-upload and backend /rag/ingestion/pdfs. */
const RAG_INGEST_UPLOAD_EXTENSIONS = new Set(['.pdf', '.docx', '.md', '.markdown']);

const RAG_COLLECTION_DEFAULT_VECTOR_SIZE = 2048;
const RAG_COLLECTION_DEFAULT_CHUNK_OVERLAP = 200;
const RAG_COLLECTION_DEFAULT_DISTANCE = 'cosine';
const RAG_COLLECTION_VECTOR_SIZE_OPTIONS = [512, 1024, 2048, 2560] as const;
const RAG_COLLECTION_CHUNK_OVERLAP_OPTIONS = [100, 200, 400] as const;
const RAG_COLLECTION_DISTANCE_OPTIONS = ['cosine', 'euclid', 'dot'] as const;

function collectionVectorSizeLabel(row: RagCollectionRow): string {
  const n = row.vector_size;
  if (n != null && n > 0) return String(n);
  return String(RAG_COLLECTION_DEFAULT_VECTOR_SIZE);
}

function collectionChunkOverlapLabel(row: RagCollectionRow): string {
  const n = row.chunk_overlap;
  if (n != null && n > 0) return String(n);
  return String(RAG_COLLECTION_DEFAULT_CHUNK_OVERLAP);
}

function collectionDistanceLabel(row: RagCollectionRow): string {
  const d = row.distance?.trim();
  if (d) return d;
  return RAG_COLLECTION_DEFAULT_DISTANCE;
}

function ragIngestFileExtensionLower(filename: string): string {
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
}

function isRagIngestUploadFile(file: File): boolean {
  return RAG_INGEST_UPLOAD_EXTENSIONS.has(ragIngestFileExtensionLower(file.name));
}

function isPdfRagUploadFile(file: File): boolean {
  return ragIngestFileExtensionLower(file.name) === '.pdf';
}

function BookStackIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function UploadCloudIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
      />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
      />
    </svg>
  );
}

function RefreshCwIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function FeishuIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
      />
    </svg>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatJobDetailTime(iso: string | undefined): string {
  if (!iso || iso.trim() === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** 构建历史「状态」列：定时重跑标识，与 JobStatusPill 同级 inline 展示 */
function JobStatusPill({ label, ok }: { label: string; ok: boolean }) {
  if (!ok) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border bg-red-50 text-red-700 border-red-200 whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
        {label}
      </span>
    );
  }
  const s = label.toLowerCase();
  const isErr = s.includes('fail') || s.includes('error');
  const isRun = s === 'running' || s === 'queued';
  const isDegraded = s.includes('upstream_missing');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap',
        isErr
          ? 'bg-red-50 text-red-800 border-red-200'
          : isRun || isDegraded
            ? 'bg-amber-50 text-amber-900 border-amber-200'
            : 'bg-emerald-50 text-emerald-800 border-emerald-200'
      )}
    >
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full shrink-0',
          isErr ? 'bg-red-500' : isRun || isDegraded ? 'bg-amber-500' : 'bg-emerald-500'
        )}
      />
      {label}
    </span>
  );
}

function jobLooksFinished(d: RagJobStatusResponse | undefined): boolean {
  if (!d) return false;
  const fin = d.finished_at;
  if (fin != null && String(fin).trim() !== '') return true;
  const s = String(d.status ?? '').toLowerCase();
  return [
    'success',
    'failed',
    'completed',
    'succeeded',
    'error',
    'done',
    'upstream_missing',
  ].includes(s);
}

function stringifyRagError(err: unknown): string {
  if (err == null || err === '') return '';
  if (typeof err === 'string') return err.trim();
  try {
    return JSON.stringify(err, null, 2);
  } catch {
    return String(err);
  }
}

/** 列表展示的状态文案是否表示失败（含 rag 异步失败后 DB 仍标记 ok 的情况） */
function statusLabelLooksFailed(label: string): boolean {
  if (label === '—') return false;
  const s = label.toLowerCase();
  return (
    /\b(fail|failed|failure|error)\b/i.test(s) ||
    s.includes('失败') ||
    s.includes('upstream_missing')
  );
}

type RagPollQueryState = {
  status: string;
  error: Error | null;
  data?: unknown;
};

function ragJobPollInterval(query: { state: RagPollQueryState }): number | false {
  if (query.state.status === 'error') return false;
  const d = query.state.data as RagJobStatusResponse | undefined;
  if (!d) return 4000;
  if (jobLooksFinished(d)) return false;
  return 4000;
}

/** One table row: batches sharing rag job_id are merged. */
interface RagGroupedRow {
  rowKey: string;
  job_id: string | null;
  collection: string;
  /** Qdrant / rags.id，用于关联 collection 说明 */
  collectionId: string;
  files: string[];
  /** Ingest task name from upstream (e.g. "sitemap:https://...") */
  name?: string;
  ok: boolean;
  statusFromDb?: string;
  error?: string;
  /** 传给删除 API 的标识：有 `job_id` 时为 rag_job_id（如 jo-…）；否则为 rag_ingest_jobs 行 id（UUID）。 */
  platformIds: string[];
  /** Min created_at among merged DB rows (RFC3339), for time column fallbacks when rag has no job. */
  dbCreatedAtMin?: string;
  /** Max created_at among merged DB rows */
  dbCreatedAtMax?: string;
}

const FAILURE_REASON_FALLBACK = '暂无详细错误信息。可点击下方「日志」查看完整输出。';

function computeBuildHistoryStatusLabel(
  row: RagGroupedRow,
  sched: { status?: string } | undefined
): string {
  if (!row.ok) {
    return row.statusFromDb && row.statusFromDb !== '' ? row.statusFromDb : '失败';
  }
  if (sched?.status && sched.status !== '') return sched.status;
  if (row.statusFromDb && row.statusFromDb !== '') return row.statusFromDb;
  return '—';
}

function buildFailureReasonMessage(
  row: RagGroupedRow,
  pollErrorDetail: string | undefined
): string {
  const db = row.error?.trim();
  const poll = pollErrorDetail?.trim();
  return db || poll || FAILURE_REASON_FALLBACK;
}

function shouldShowFailureReasonHint(row: RagGroupedRow, statusLabel: string): boolean {
  return (
    !row.ok ||
    Boolean(row.error?.trim()) ||
    statusLabelLooksFailed(statusLabel) ||
    statusLabel.toLowerCase().includes('upstream_missing')
  );
}

function isFeishuIngestName(name: string | undefined): boolean {
  return (name?.trim() ?? '').startsWith('feishu:');
}

/** Convert a 5-field cron expression to a human-readable interval label. */
function cronToIntervalLabel(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const min = parts[0];
  const hour = parts[1];
  const dom = parts[2]; // day-of-month field

  // "*/5 * * * *" → every 5 minutes
  const minMatch = min.match(/^\*\/(\d+)$/);
  if (minMatch && hour === '*') {
    return `每 ${minMatch[1]} 分钟`;
  }
  // "0 */12 * * *" → every 12 hours
  const hourMatch = hour.match(/^\*\/(\d+)$/);
  if (hourMatch && dom === '*') {
    const n = parseInt(hourMatch[1], 10);
    if (n === 1) return '每小时';
    return `每 ${n} 小时`;
  }
  // "0 0 * * *" → daily, "0 0 */2 * *" → every 2 days, etc.
  if (dom === '*') {
    // "0 * * * *"（每小时整点）的 hour 是 "*" 而非 "*/1"，不加这一条会掉进下面的
    // dom === '*' 分支被标成「每天」——面板上会出现「每天」却「下次 21:00」的自相矛盾。
    if (hour === '*') return '每小时';
    return '每天';
  }
  const m = dom.match(/^\*\/(\d+)$/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n === 1) return '每天';
    return `每 ${n} 天`;
  }
  return cron;
}

function parseIsoTimeMs(iso: string | undefined): number | null {
  if (!iso || iso.trim() === '') return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function formatRelativeTimeZh(iso: string | undefined): string {
  const ms = parseIsoTimeMs(iso);
  if (ms == null) return '';
  const diffSec = Math.round((Date.now() - ms) / 1000);
  if (diffSec < 0) return '刚刚';
  if (diffSec < 60) return `${diffSec} 秒前`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} 分钟前`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 48) return `${diffHour} 小时前`;
  const diffDay = Math.round(diffHour / 24);
  return `${diffDay} 天前`;
}

function cronIntervalMs(cron: string): number | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const minMatch = parts[0].match(/^\*\/(\d+)$/);
  if (minMatch && parts[1] === '*') {
    return parseInt(minMatch[1], 10) * 60 * 1000;
  }
  const hourMatch = parts[1].match(/^\*\/(\d+)$/);
  if (hourMatch && parts[2] === '*') {
    return parseInt(hourMatch[1], 10) * 60 * 60 * 1000;
  }
  if (parts[2] === '*') {
    // "0 * * * *" 是每小时；不单独判会被当成每天，overdue 判定放宽 24 倍。
    if (parts[1] === '*') return 60 * 60 * 1000;
    return 24 * 60 * 60 * 1000;
  }
  const domMatch = parts[2].match(/^\*\/(\d+)$/);
  if (domMatch) return parseInt(domMatch[1], 10) * 24 * 60 * 60 * 1000;
  return null;
}

type CronExecutionHealth = 'never' | 'ok' | 'overdue' | 'failed';

function cronExecutionHealth(job: RagRerunJob): CronExecutionHealth {
  // 失败的 JobRun 同样有 StartedAt，last_fired_at 是新的，只看触发时间会把
  // 「按时触发但每次都失败」判成健康。故先看最近一次执行结果。
  if (job.last_run_status?.trim() === 'JOB_RUN_STATUS_FAILED') return 'failed';
  const lastMs = parseIsoTimeMs(job.last_fired_at);
  const interval = cronIntervalMs(job.cron);
  if (lastMs == null) {
    const createdMs = parseIsoTimeMs(job.created_at);
    if (createdMs != null && interval != null && Date.now() - createdMs > interval * 2) {
      return 'overdue';
    }
    return 'never';
  }
  if (interval == null) return 'ok';
  if (Date.now() - lastMs > interval * 2.5) return 'overdue';
  return 'ok';
}

// cronLastRunHint 描述「最新任务」缺失时该显示什么：cron 从未触发、正在执行、
// 触发了但被跳过、或者触发后失败。之前这里一律显示「等待首次定时触发」，
// 失败和跳过都被说成「还没触发过」，排查时会被误导。
function cronLastRunHint(job: RagRerunJob): {
  text: string;
  detail?: string;
  tone: 'muted' | 'error';
} {
  const status = job.last_run_status?.trim() ?? '';
  const result = job.last_run_result?.trim() ?? '';
  const errMsg = job.last_run_error?.trim() ?? '';
  const firedAt = job.last_fired_at ? formatJobDetailTime(job.last_fired_at) : '';

  if (status === '' && !job.last_fired_at) {
    return { text: '等待首次定时触发后将在此展示最新任务', tone: 'muted' };
  }
  if (status === 'JOB_RUN_STATUS_FAILED') {
    const reason = errMsg || result || '未知原因';
    return { text: `上次执行失败（${firedAt}）：${reason}`, detail: reason, tone: 'error' };
  }
  if (status === 'JOB_RUN_STATUS_RUNNING' || status === 'JOB_RUN_STATUS_PENDING') {
    return { text: `上次触发（${firedAt}）正在执行，稍后展示最新任务`, tone: 'muted' };
  }
  if (status === 'JOB_RUN_STATUS_COMPLETED') {
    // 完成但没拿到 ingest job id：多为蓝绿任务仍在进行而本次被跳过。
    const reason = result || '未生成构建任务';
    return {
      text: `上次执行（${firedAt}）未生成构建任务：${reason}`,
      detail: reason,
      tone: 'muted',
    };
  }
  return { text: `上次触发于 ${firedAt}，暂无最新任务`, tone: 'muted' };
}

function rowMatchesRerunJob(row: RagGroupedRow, job: RagRerunJob): boolean {
  const coll = row.collectionId.trim();
  if (coll === '' || coll !== job.collection_id.trim()) return false;
  const name = row.name?.trim() ?? '';
  if (job.kind === 'sitemap') {
    const sitemap = job.sitemap_url?.trim() ?? '';
    if (sitemap !== '' && name === `sitemap:${sitemap}`) return true;
    return name.startsWith('sitemap:');
  }
  if (job.kind === 'web') return name.startsWith('web:');
  return false;
}

function findRerunJobForRow(row: RagGroupedRow, jobs: RagRerunJob[]): RagRerunJob | undefined {
  return jobs.find((j) => rowMatchesRerunJob(row, j));
}

function findCronJobByIngestJobId(
  ingestJobId: string | null | undefined,
  jobs: RagRerunJob[]
): RagRerunJob | undefined {
  const id = ingestJobId?.trim() ?? '';
  if (id === '') return undefined;
  return jobs.find((j) => (j.last_ingest_job_id?.trim() ?? '') === id);
}

/** 构建历史行是否由定时任务触发（对比 cron last_fired_at 与任务开始时间） */
function isScheduledTrigger(
  row: RagGroupedRow,
  job: RagRerunJob | undefined,
  startedIso: string | undefined
): boolean {
  if (job && row.job_id && (job.last_ingest_job_id?.trim() ?? '') === row.job_id.trim()) {
    return true;
  }
  if (!job || !rowMatchesRerunJob(row, job)) return false;
  const lastFiredMs = parseIsoTimeMs(job.last_fired_at);
  const startedMs = parseIsoTimeMs(startedIso ?? row.dbCreatedAtMin);
  if (lastFiredMs == null || startedMs == null) return false;
  return Math.abs(startedMs - lastFiredMs) <= 15 * 60 * 1000;
}

function resolveBuildHistoryTrigger(
  row: RagGroupedRow,
  activeRerunJobs: RagRerunJob[],
  startedIso: string | undefined
): 'scheduled' | 'manual' | null {
  if (!canRerunBuildHistoryRow(row).ok) return null;
  const cronByIngest = findCronJobByIngestJobId(row.job_id, activeRerunJobs);
  if (cronByIngest) return 'scheduled';
  const job = findRerunJobForRow(row, activeRerunJobs);
  if (job && isScheduledTrigger(row, job, startedIso)) return 'scheduled';
  return 'manual';
}

function cronJobDocFiles(job: RagRerunJob): string[] {
  if (job.sitemap_url?.trim()) return [job.sitemap_url.trim()];
  if (job.urls && job.urls.length > 0) return job.urls;
  return ['—'];
}

function cronJobIngestName(job: RagRerunJob): string | undefined {
  if (job.kind === 'sitemap' && job.sitemap_url?.trim()) {
    return `sitemap:${job.sitemap_url.trim()}`;
  }
  if (job.urls && job.urls.length > 0) {
    return `web:${job.urls.join(',')}`;
  }
  return undefined;
}

/** 将定时任务最新 ingest job 补入构建历史（上游列表可能尚未同步或已被 cleanup 替换） */
function mergeCronLatestIntoGroupedRows(
  base: RagGroupedRow[],
  activeRerunJobs: RagRerunJob[],
  collectionDisplayByQdrantId: Map<string, string>
): RagGroupedRow[] {
  const seenJobIds = new Set(base.map((r) => r.job_id?.trim() ?? '').filter((id) => id !== ''));
  const extras: RagGroupedRow[] = [];
  for (const job of activeRerunJobs) {
    const jid = job.last_ingest_job_id?.trim() ?? '';
    if (jid === '' || seenJobIds.has(jid)) continue;
    seenJobIds.add(jid);
    const collId = job.collection_id.trim();
    const collDisplay =
      collectionDisplayByQdrantId.get(collId)?.trim() || job.collection_id.trim() || '—';
    const firedAt = job.last_fired_at?.trim() ?? '';
    extras.push({
      rowKey: `cron-latest-${job.id}-${jid}`,
      job_id: jid,
      collection: collDisplay,
      collectionId: collId,
      files: cronJobDocFiles(job),
      name: cronJobIngestName(job),
      ok: true,
      platformIds: [jid],
      dbCreatedAtMin: firedAt !== '' ? firedAt : undefined,
      dbCreatedAtMax: firedAt !== '' ? firedAt : undefined,
    });
  }
  if (extras.length === 0) return base;
  return [...extras, ...base];
}

function BuildHistoryScheduledBadge() {
  return (
    <span
      className="inline-flex shrink-0 items-center rounded border border-blue-300/60 bg-blue-50/80 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:border-blue-600/40 dark:bg-blue-900/25 dark:text-blue-300"
      title="由活跃定时任务自动触发"
    >
      定时
    </span>
  );
}

function canRerunBuildHistoryRow(row: RagGroupedRow): { ok: boolean; reason?: string } {
  const name = row.name?.trim() ?? '';
  if (name.startsWith('feishu:') || name.startsWith('sitemap:') || name.startsWith('web:')) {
    return { ok: true };
  }
  if (row.files.length > 0 && row.files.every((f) => /^https?:\/\//i.test(f.trim()))) {
    return { ok: true };
  }
  return { ok: false, reason: '本地上传的文件任务无法重跑，请重新上传文件' };
}

function buildRerunSummary(row: RagGroupedRow): string {
  const name = row.name?.trim() ?? '';
  if (name.startsWith('sitemap:')) {
    return '将先删除本任务在 Qdrant 中的向量与构建记录，再重新解析 sitemap 并提交摄入。';
  }
  if (name.startsWith('feishu:')) {
    return '将先删除本任务在 Qdrant 中的向量与构建记录，再使用当前飞书授权重新提交摄入。';
  }
  return '将先删除本任务在 Qdrant 中的向量与构建记录，再使用相同 URL 重新提交摄入。';
}

function isNonTerminalBuildStatus(label: string): boolean {
  const s = label.trim().toLowerCase();
  if (s === '' || s === '—') return false;
  return ['queued', 'pending', 'running', 'processing', 'started', 'working'].includes(s);
}

function RagJobStatusCell({
  row,
  statusLabel,
  pollErrorDetail,
  onOpenFailureReason,
}: {
  row: RagGroupedRow;
  statusLabel: string;
  pollErrorDetail: string | undefined;
  onOpenFailureReason: (detail: string) => void;
}) {
  const showHint = shouldShowFailureReasonHint(row, statusLabel);
  const explainMessage = buildFailureReasonMessage(row, pollErrorDetail);
  const pillLabel = statusLabel !== '—' ? statusLabel : '失败';

  if (showHint) {
    const pillOk = row.ok || pillLabel.toLowerCase().includes('upstream_missing');
    return (
      <div className="inline-flex flex-wrap items-center gap-1.5">
        <JobStatusPill label={pillLabel} ok={pillOk} />
        <button
          type="button"
          className="inline-flex shrink-0 items-center justify-center px-0.5 text-xs font-semibold leading-none text-red-700 hover:text-red-900 hover:underline underline-offset-2"
          aria-label="查看失败原因"
          title="查看失败原因"
          onClick={() => onOpenFailureReason(explainMessage)}
        >
          ?
        </button>
      </div>
    );
  }

  if (statusLabel !== '—') {
    return <JobStatusPill label={statusLabel} ok />;
  }

  return <span className="text-xs text-muted-foreground">—</span>;
}

/** 说明超过两行时，第二行末尾「展开」；展开后全文 +「收起」 */
function DescriptionClampExpand({
  text,
  className,
  textClassName,
}: {
  text: string;
  className?: string;
  textClassName?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [needsClamp, setNeedsClamp] = useState(false);
  const measureRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setExpanded(false);
  }, [text]);

  const updateNeedsClamp = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;
    setNeedsClamp(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    if (expanded) return;
    updateNeedsClamp();
  }, [expanded, text, updateNeedsClamp]);

  useEffect(() => {
    if (expanded) return;
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateNeedsClamp());
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, text, updateNeedsClamp]);

  if (expanded) {
    return (
      <div className={cn('min-w-0', className)}>
        <p className={cn('break-words whitespace-pre-wrap leading-relaxed', textClassName)}>
          {text}
        </p>
        <button
          type="button"
          className="mt-1 text-xs font-medium text-primary hover:underline"
          aria-expanded
          onClick={() => setExpanded(false)}
        >
          收起
        </button>
      </div>
    );
  }

  return (
    <div className={cn('relative min-w-0', className)}>
      <p ref={measureRef} className={cn('break-words leading-relaxed line-clamp-2', textClassName)}>
        {text}
      </p>
      {needsClamp ? (
        <span className="pointer-events-none absolute bottom-0 right-0 z-[1] inline-flex max-w-[min(12rem,55%)] items-end justify-end bg-gradient-to-l from-background from-35% via-background/90 to-transparent pl-6">
          <button
            type="button"
            className="pointer-events-auto shrink-0 text-xs font-medium text-primary hover:underline"
            aria-expanded={false}
            onClick={() => setExpanded(true)}
          >
            展开
          </button>
        </span>
      ) : null}
    </div>
  );
}

/** 构建历史「文档」列：一行展示不下时右侧显示「展开 / 收起」 */
function BuildHistoryDocCell({
  files,
  name,
  rowKey,
  expanded,
  onToggle,
}: {
  files: string[];
  name?: string;
  rowKey: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  // When name starts with "sitemap:", show the sitemap URL instead of file list
  const sitemapUrl = name?.startsWith('sitemap:') ? name.slice('sitemap:'.length) : undefined;
  const collapsedText = files.join('、');
  const [overflows, setOverflows] = useState(false);
  const measureRef = useRef<HTMLParagraphElement>(null);

  const updateOverflow = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    if (sitemapUrl || expanded) return;
    updateOverflow();
  }, [sitemapUrl, expanded, collapsedText, updateOverflow]);

  useEffect(() => {
    if (sitemapUrl || expanded) return;
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [sitemapUrl, expanded, collapsedText, updateOverflow]);

  if (sitemapUrl) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <GlobeIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <a
          href={sitemapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-foreground hover:text-primary truncate whitespace-nowrap"
          title={sitemapUrl}
        >
          {sitemapUrl}
        </a>
      </div>
    );
  }

  if (files.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const showToggle = expanded || overflows;

  if (!showToggle) {
    return (
      <p
        ref={measureRef}
        className="text-xs text-foreground line-clamp-1 break-all"
        title={collapsedText}
      >
        {collapsedText}
      </p>
    );
  }

  return (
    <div className={cn('flex min-w-0 gap-1.5', expanded ? 'items-start' : 'items-center')}>
      <div className="min-w-0 flex-1">
        {!expanded ? (
          <p
            ref={measureRef}
            className="text-xs text-foreground line-clamp-1 break-all"
            title={collapsedText}
          >
            {collapsedText}
          </p>
        ) : (
          <div className="space-y-1">
            {files.map((f) => (
              <div
                key={`${rowKey}-${f}`}
                className="text-xs text-foreground leading-snug break-all"
                title={f}
              >
                {f}
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className="shrink-0 self-center text-[11px] font-medium text-primary hover:underline"
        aria-expanded={expanded}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      >
        {expanded ? '收起' : '展开'}
      </button>
    </div>
  );
}

/** 表格单元格常有 overflow:hidden，原生 title 不可靠；用 portal 固定定位展示说明 */
function HoverTooltip({ text, children }: { text: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxTooltipPx = 320;
    const pad = 8;
    let left = r.left;
    const vw = window.innerWidth;
    if (left + maxTooltipPx + pad > vw) {
      left = Math.max(pad, vw - maxTooltipPx - pad);
    }
    setPos({ top: r.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onMove = () => updatePos();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, updatePos]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex shrink-0 cursor-default items-center justify-center rounded p-0.5 text-muted-foreground outline-none hover:bg-muted/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        tabIndex={0}
        aria-label={`说明：${text}`}
        onMouseEnter={() => {
          updatePos();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          updatePos();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[200] max-w-sm rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-snug text-popover-foreground shadow-md [max-width:min(20rem,calc(100vw-1rem))] whitespace-pre-wrap break-words"
              style={{ top: pos.top, left: pos.left }}
            >
              {text}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

// RagJobLogPanel 是 ingest 任务的日志面板。构建历史表格行与「活跃定时任务」面板共用同一份
// 流状态（logText / isStreaming / logPreRef 都是单例），因此同一时刻只允许渲染一处。
function RagJobLogPanel({
  fileLabel,
  streaming,
  logText,
  streamError,
  preRef,
}: {
  fileLabel: string;
  streaming: boolean;
  logText: string;
  streamError: string | null;
  preRef: MutableRefObject<HTMLPreElement | null>;
}) {
  return (
    <div className="rounded-lg border border-primary/20 bg-zinc-950 text-zinc-100 shadow-inner overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-medium text-zinc-300 truncate">{fileLabel || '—'}</span>
        </div>
        {streaming && (
          <span className="flex items-center gap-1.5 text-[10px] text-amber-400 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            接收中
          </span>
        )}
      </div>
      <pre
        ref={preRef}
        className="max-h-[min(420px,50vh)] overflow-auto px-4 py-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words"
      >
        {streamError ? (
          <span className="text-red-400">{streamError}</span>
        ) : (
          <>
            {redactDocIdsInLogDisplay(logText)}
            {streaming && !logText.trim() ? '等待输出…' : null}
          </>
        )}
      </pre>
    </div>
  );
}

function formatRagApiError(err: unknown): string {
  // httpv2 业务错误统一由 apiClient 拦截器抛成 ApiError，message 已是收敛后的中文文案。
  if (err instanceof ApiError) {
    return err.message;
  }
  // 兼容仍走 v1 的飞书公共路由（axios 错误：{ response: { data: { error } } }）。
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'object' && data !== null && 'error' in data) {
      const msg = (data as { error: unknown }).error;
      if (typeof msg === 'string' && msg.trim() !== '') return msg;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

function collectionDisplayLabel(r: RagPdfJobResult | undefined): string {
  if (!r) return '—';
  const name = r.collection_display_name?.trim();
  if (name) return name;
  return r.qdrant_collection_name?.trim() || '—';
}

function ingestRowCreatedBounds(members: RagPdfJobResult[]): { min?: string; max?: string } {
  const dates = members
    .map((m) => m.created_at)
    .filter((s): s is string => typeof s === 'string' && s.trim() !== '');
  if (dates.length === 0) return {};
  let min = dates[0];
  let max = dates[0];
  for (const s of dates) {
    if (s < min) min = s;
    if (s > max) max = s;
  }
  return { min, max };
}

function groupRagIngestRows(rows: RagPdfJobResult[]): RagGroupedRow[] {
  const out: RagGroupedRow[] = [];
  const seenJob = new Set<string>();
  for (const r of rows) {
    const jid = r.job_id?.trim();
    if (jid) {
      if (seenJob.has(jid)) continue;
      seenJob.add(jid);
      const members = rows.filter((x) => (x.job_id?.trim() ?? '') === jid);
      const bounds = ingestRowCreatedBounds(members);
      const ingestDeleteRefs = [jid];
      const allOk = members.every((m) => m.ok);
      const firstErr = members.find((m) => (m.error ?? '').trim() !== '')?.error;
      out.push({
        rowKey: jid,
        job_id: jid,
        collection: collectionDisplayLabel(members[0]),
        collectionId: members[0]?.qdrant_collection_name?.trim() ?? '',
        files: members.map((m) => m.original_filename),
        name: members.find((m) => m.name?.trim())?.name?.trim() || undefined,
        ok: allOk,
        statusFromDb: members[0]?.status,
        error: firstErr,
        platformIds: ingestDeleteRefs,
        dbCreatedAtMin: bounds.min,
        dbCreatedAtMax: bounds.max,
      });
    } else {
      const bounds = ingestRowCreatedBounds([r]);
      out.push({
        rowKey: r.id ?? `fail-${r.original_filename}-${r.created_at ?? ''}`,
        job_id: null,
        collection: collectionDisplayLabel(r),
        collectionId: r.qdrant_collection_name?.trim() ?? '',
        files: [r.original_filename],
        name: r.name?.trim() || undefined,
        ok: r.ok,
        error: r.error,
        statusFromDb: r.status,
        platformIds: r.id && r.id.trim() !== '' ? [r.id] : [],
        dbCreatedAtMin: bounds.min,
        dbCreatedAtMax: bounds.max,
      });
    }
  }
  return out;
}

/** 构建历史表：选择框、任务 ID、Collection、文档、状态、开始、结束、操作（默认比例基准为 px） */
const HISTORY_BUILD_COL_DEFAULTS: readonly number[] = [40, 160, 120, 156, 112, 148, 148, 168];
const HISTORY_BUILD_COL_MINS: readonly number[] = [32, 80, 72, 100, 88, 110, 110, 120];
const HISTORY_BUILD_COL_MAXS: readonly number[] = [72, 384, 360, 560, 280, 260, 260, 280];

const HISTORY_BUILD_COL_FRAC0: readonly number[] = (() => {
  const s = HISTORY_BUILD_COL_DEFAULTS.reduce((a, b) => a + b, 0);
  return HISTORY_BUILD_COL_DEFAULTS.map((w) => w / s);
})();

/** 将比例转为 col 用的百分比，保证最后一列吃满余数，总和为 100 */
function buildHistoryColPercents(fracs: readonly number[]): number[] {
  const n = fracs.length;
  if (n === 0) return [];
  const pct = fracs.map((f) => f * 100);
  let acc = 0;
  const out: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const v = Math.round(pct[i] * 10000) / 10000;
    out.push(v);
    acc += v;
  }
  out.push(Math.round((100 - acc) * 10000) / 10000);
  return out;
}

/** 某一列变为 newKpx 后，其余列按原宽度等比分剩余空间，并满足 min/max；返回各列像素宽（和为 cw） */
function redistributeHistoryColWidthsPx(
  cw: number,
  k: number,
  newKpx: number,
  startPx: readonly number[],
  mins: readonly number[],
  maxs: readonly number[]
): number[] {
  const n = startPx.length;
  const out = new Array<number>(n).fill(0);
  newKpx = Math.min(maxs[k] ?? cw, Math.max(mins[k] ?? 0, Math.round(newKpx)));
  out[k] = newKpx;
  const others = Array.from({ length: n }, (_, i) => i).filter((i) => i !== k);
  const pool = cw - newKpx;
  const sumOthersStart = others.reduce((s, i) => s + startPx[i], 0);
  if (sumOthersStart <= 0) {
    const even = pool / others.length;
    for (const i of others) {
      out[i] = Math.min(maxs[i] ?? cw, Math.max(mins[i] ?? 0, Math.round(even)));
    }
  } else {
    for (const i of others) {
      let v = pool * (startPx[i] / sumOthersStart);
      v = Math.round(v);
      out[i] = Math.min(maxs[i] ?? cw, Math.max(mins[i] ?? 0, v));
    }
  }
  const sum = out.reduce((a, b) => a + b, 0);
  let drift = Math.round(cw - sum);
  let guard = 0;
  const order = drift > 0 ? [...others, k] : [k, ...others];
  while (drift !== 0 && guard++ < 128) {
    const cand = order.find((i) =>
      drift > 0 ? out[i] < (maxs[i] ?? cw) : out[i] > (mins[i] ?? 0)
    );
    if (cand === undefined) break;
    out[cand] += drift > 0 ? 1 : -1;
    drift += drift > 0 ? -1 : 1;
  }
  return out;
}

function HistoryTableColResizeHandle({
  columnIndex,
  containerRef,
  fracsRef,
  setFracs,
}: {
  columnIndex: number;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  fracsRef: MutableRefObject<number[]>;
  setFracs: Dispatch<SetStateAction<number[]>>;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="拖动调整列宽"
      className="absolute right-0 top-0 z-[2] h-full w-3 max-w-[14px] translate-x-1/2 cursor-col-resize select-none touch-none rounded-sm hover:bg-primary/20 active:bg-primary/30"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const handle = e.currentTarget;
        const wrap = containerRef.current;
        if (!wrap) return;
        const cw = Math.max(320, wrap.clientWidth);
        const startFracs = [...fracsRef.current];
        const startPx = startFracs.map((f) => f * cw);
        const startX = e.clientX;
        handle.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent) => {
          const newKpx = startPx[columnIndex] + (ev.clientX - startX);
          const nextPx = redistributeHistoryColWidthsPx(
            cw,
            columnIndex,
            newKpx,
            startPx,
            HISTORY_BUILD_COL_MINS,
            HISTORY_BUILD_COL_MAXS
          );
          const nextFracs = nextPx.map((px) => px / cw);
          setFracs(nextFracs);
        };
        const end = (ev: PointerEvent) => {
          try {
            if (handle.hasPointerCapture(ev.pointerId)) {
              handle.releasePointerCapture(ev.pointerId);
            }
          } catch {
            /* ignore */
          }
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', end);
          window.removeEventListener('pointercancel', end);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
      }}
    />
  );
}

type RagsBuildLocationState = {
  uploadMode?: 'file' | 'feishu' | 'web';
};

function readUploadModeFromLocation(state: unknown): 'feishu' | 'web' | null {
  const uploadMode = (state as RagsBuildLocationState | null)?.uploadMode;
  if (uploadMode === 'feishu') return 'feishu';
  if (uploadMode === 'web') return 'web';
  return null;
}

export default function RAGsPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const logPreRef = useRef<HTMLPreElement | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  const [uploadMode, setUploadMode] = useState<'file' | 'feishu' | 'web'>(() => {
    return readUploadModeFromLocation(location.state) ?? 'file';
  });
  const [feishuDocumentId, setFeishuDocumentId] = useState('');
  const [feishuRecursive, setFeishuRecursive] = useState(false);
  const [webUrlsText, setWebUrlsText] = useState('');
  const [webInputMode, setWebInputMode] = useState<'sitemap' | 'manual'>('sitemap');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [sitemapParsedUrls, setSitemapParsedUrls] = useState<string[]>([]);
  const [sitemapUnreachableCount, setSitemapUnreachableCount] = useState(0);
  const [autoRerunIntervalHours, setAutoRerunIntervalHours] = useState<number>(0);
  const {
    feishuUserToken,
    feishuAppId,
    feishuAppSecret,
    feishuDevSpace,
    clearFeishuToken,
    setFeishuAppConfig,
    clearFeishuConfig,
    isFeishuTokenValid,
    isFeishuConfigured,
  } = useFeishuAuthStore();
  const { token } = useAuthStore();
  const isFeishuLoggedIn = isFeishuTokenValid();
  const isFeishuReady = isFeishuConfigured();
  const feishuRedirectUri = `${window.location.origin}${(apiClient.defaults.baseURL as string | undefined)?.replace(/\/+$/, '') || '/api'}/public/rag/feishu/callback`;

  const [collectionName, setCollectionName] = useState('');
  const [newCollectionDisplayName, setNewCollectionDisplayName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newCollectionVectorSize, setNewCollectionVectorSize] = useState('');
  const [newCollectionChunkOverlap, setNewCollectionChunkOverlap] = useState('');
  const [newCollectionDistance, setNewCollectionDistance] = useState('');
  const [newCollectionInputOpen, setNewCollectionInputOpen] = useState(false);
  const newCollectionInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const selectedFilesRef = useRef<File[]>([]);
  const [isCheckingPdfPages, setIsCheckingPdfPages] = useState(false);
  const pdfPageCheckLockRef = useRef(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [logsExpandedJobId, setLogsExpandedJobId] = useState<string | null>(null);
  // 日志流状态是单例（logText / logPreRef 只有一份），所以同一时刻只能有一处渲染日志面板。
  // logsExpandedSource 记录展开来源：'cron' 渲染在「活跃定时任务」面板内，'history' 渲染在构建历史表格行内。
  const [logsExpandedSource, setLogsExpandedSource] = useState<'cron' | 'history'>('history');
  const [streamJobId, setStreamJobId] = useState<string | null>(null);
  const [streamFileLabel, setStreamFileLabel] = useState('');
  const [logText, setLogText] = useState('');
  const [streamError, setStreamError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set());
  const [docsExpandedRowKeys, setDocsExpandedRowKeys] = useState<Set<string>>(() => new Set());
  const headerSelectRef = useRef<HTMLInputElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    platformIds: string[];
    ragJobIdsToCloseLogs: string[];
    summary: string;
  } | null>(null);
  const [confirmRerun, setConfirmRerun] = useState<{
    platformId: string;
    ragJobIdToCloseLogs: string | null;
    summary: string;
    needsFeishu: boolean;
    /** Fallback name for rerun when upstream row is missing */
    name?: string;
    /** Fallback qdrant_collection_name for rerun when upstream row is missing */
    qdrantCollectionName?: string;
  } | null>(null);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<string>>(() => new Set());
  const [myCollectionsSectionExpanded, setMyCollectionsSectionExpanded] = useState(true);
  const [batchDeleteCollectionsDialog, setBatchDeleteCollectionsDialog] = useState<
    RagCollectionRow[] | null
  >(null);
  const collectionListHeaderSelectRef = useRef<HTMLInputElement | null>(null);
  const [failureReasonDialog, setFailureReasonDialog] = useState<string | null>(null);
  const [filePreviewTarget, setFilePreviewTarget] = useState<File | null>(null);
  const [filePreviewResult, setFilePreviewResult] = useState<RagFilePreviewResult | null>(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState(false);
  const [filePreviewError, setFilePreviewError] = useState<string | null>(null);
  const filePreviewRequestIdRef = useRef(0);
  const [historyCollectionFilter, setHistoryCollectionFilter] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('');
  const [historyFilterMenuOpen, setHistoryFilterMenuOpen] = useState<
    'collection' | 'status' | null
  >(null);
  const [historyFilterDropdownPos, setHistoryFilterDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const historyFilterCollectionBtnRef = useRef<HTMLButtonElement | null>(null);
  const historyFilterStatusBtnRef = useRef<HTMLButtonElement | null>(null);
  const historyFilterDropdownRef = useRef<HTMLDivElement | null>(null);
  const historyTableWrapRef = useRef<HTMLDivElement | null>(null);
  const [historyTableColFracs, setHistoryTableColFracs] = useState<number[]>(() => [
    ...HISTORY_BUILD_COL_FRAC0,
  ]);
  const [buildHistoryRefreshing, setBuildHistoryRefreshing] = useState(false);
  const historyTableColFracsRef = useRef(historyTableColFracs);
  historyTableColFracsRef.current = historyTableColFracs;
  const historyTableColPercents = useMemo(
    () => buildHistoryColPercents(historyTableColFracs),
    [historyTableColFracs]
  );

  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    const modeFromLocation = readUploadModeFromLocation(location.state);
    if (modeFromLocation === null) {
      return;
    }
    setUploadMode(modeFromLocation);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  const rerunJobsQuery = useQuery({
    queryKey: ['rag-rerun-jobs'],
    queryFn: () => ragApi.listRerunJobs(),
    enabled: true,
    staleTime: 15_000,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      return jobs.length > 0 ? 15_000 : false;
    },
  });

  /** Frontend debug flag — when off, hide 5min/1h auto-rerun intervals from the UI. */
  const isDebug = import.meta.env.VITE_ENABLE_DEBUG === 'true';

  useEffect(() => {
    if (!isDebug && (autoRerunIntervalHours === -1 || autoRerunIntervalHours === 1)) {
      setAutoRerunIntervalHours(0);
    }
  }, [isDebug, autoRerunIntervalHours]);

  /** Active RAG_RERUN cron jobs, ready for display in the panel. */
  const activeRerunJobs = useMemo(
    () => rerunJobsQuery.data?.jobs ?? [],
    [rerunJobsQuery.data?.jobs]
  );

  const jobsQuery = useQuery({
    queryKey: ['rag-ingest-jobs'],
    queryFn: () => ragApi.listRagIngestJobs({ page: 1, pageSize: 200 }),
    // Auto-refresh when there are active cron jobs so new ingest records appear
    refetchInterval: activeRerunJobs.length > 0 ? 15_000 : false,
  });

  const collectionsQuery = useQuery({
    queryKey: ['rag-collections'],
    queryFn: () => ragApi.listRagCollections({ page: 1, pageSize: 200 }),
    enabled: true,
  });

  const createCollectionMutation = useMutation({
    mutationFn: (vars: {
      displayName: string;
      description: string;
      vectorSize?: number;
      chunkOverlap?: number;
      distance?: string;
    }) =>
      ragApi.createRagCollection({
        collection_display_name: vars.displayName,
        description: vars.description.trim() || undefined,
        vector_size: vars.vectorSize,
        chunk_overlap: vars.chunkOverlap,
        distance: vars.distance,
      }),
    onSuccess: (created) => {
      setCollectionName(created.collection_name);
      setNewCollectionDisplayName('');
      setNewCollectionDescription('');
      setNewCollectionVectorSize('');
      setNewCollectionChunkOverlap('');
      setNewCollectionDistance('');
      setNewCollectionInputOpen(false);
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey: ['rag-collections'] });
    },
    onError: (err: unknown) => {
      setLocalError(err instanceof Error ? err.message : String(err));
    },
  });

  const deleteCollectionsBatchMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const settled = await Promise.allSettled(ids.map((id) => ragApi.deleteRagCollection(id)));
      const deletedIds: string[] = [];
      const failed: { id: string; err: string }[] = [];
      settled.forEach((s, i) => {
        const id = ids[i];
        if (s.status === 'fulfilled') deletedIds.push(id);
        else failed.push({ id, err: formatRagApiError(s.reason) });
      });
      return { deletedIds, failed };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['rag-collections'] });
      setCollectionName((prev) => (data.deletedIds.includes(prev.trim()) ? '' : prev));
      setSelectedCollectionIds((prev) => {
        const next = new Set(prev);
        for (const id of data.deletedIds) next.delete(id);
        return next;
      });
      setBatchDeleteCollectionsDialog(null);
      if (data.failed.length > 0) {
        setLocalError(
          data.deletedIds.length > 0
            ? `已删除 ${data.deletedIds.length} 个；${data.failed.length} 个失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
            : `删除失败：\n${data.failed.map((f) => `· ${f.id}：${f.err}`).join('\n')}`
        );
      } else {
        setLocalError(null);
      }
    },
  });

  useEffect(() => {
    if (newCollectionInputOpen) {
      queueMicrotask(() => newCollectionInputRef.current?.focus());
    }
  }, [newCollectionInputOpen]);

  const collectionsData = collectionsQuery.data?.collections;
  const collectionRows = useMemo(() => collectionsData ?? [], [collectionsData]);

  useEffect(() => {
    const list = collectionsData ?? [];
    const valid = new Set(list.map((c) => c.id));
    setSelectedCollectionIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [collectionsData]);

  const allCollectionsSelected =
    collectionRows.length > 0 && collectionRows.every((c) => selectedCollectionIds.has(c.id));
  const someCollectionsSelected = collectionRows.some((c) => selectedCollectionIds.has(c.id));

  useLayoutEffect(() => {
    const el = collectionListHeaderSelectRef.current;
    if (!el) return;
    el.indeterminate = someCollectionsSelected && !allCollectionsSelected;
  }, [someCollectionsSelected, allCollectionsSelected]);

  const toggleCollectionRowSelected = useCallback((id: string) => {
    setSelectedCollectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllCollections = useCallback(() => {
    setSelectedCollectionIds((prev) => {
      if (collectionRows.length === 0) return prev;
      if (collectionRows.every((c) => prev.has(c.id))) {
        return new Set();
      }
      return new Set(collectionRows.map((c) => c.id));
    });
  }, [collectionRows]);

  const results: RagPdfJobResult[] = useMemo(
    () => (jobsQuery.data?.jobs ?? []).map(ragIngestRowToResult),
    [jobsQuery.data?.jobs]
  );

  const collectionDisplayByQdrantId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of collectionsQuery.data?.collections ?? []) {
      const qdName = c.qdrant_collection_name?.trim();
      if (!qdName) continue;
      m.set(qdName, c.collection_display_name?.trim() || c.id?.trim() || qdName);
    }
    return m;
  }, [collectionsQuery.data?.collections]);

  const pollJobIds = useMemo(() => {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const job of activeRerunJobs) {
      const cronJid = job.last_ingest_job_id?.trim();
      if (cronJid && !seen.has(cronJid)) {
        seen.add(cronJid);
        ids.push(cronJid);
      }
    }
    for (const r of results) {
      if (r.ok && r.job_id && !seen.has(r.job_id)) {
        seen.add(r.job_id);
        ids.push(r.job_id);
      }
    }
    return ids;
  }, [results, activeRerunJobs]);

  const groupedIngestRows = useMemo(
    () =>
      mergeCronLatestIntoGroupedRows(
        groupRagIngestRows(results),
        activeRerunJobs,
        collectionDisplayByQdrantId
      ),
    [results, activeRerunJobs, collectionDisplayByQdrantId]
  );
  const groupedIngestRowsRef = useRef<RagGroupedRow[]>([]);
  groupedIngestRowsRef.current = groupedIngestRows;

  const collectionDescriptionByQdrantId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of collectionsQuery.data?.collections ?? []) {
      const qdName = c.qdrant_collection_name?.trim();
      if (!qdName) continue;
      const d = c.description?.trim();
      m.set(qdName, d ?? '');
    }
    return m;
  }, [collectionsQuery.data?.collections]);

  const hasUploadCollectionSelected = collectionName.trim() !== '';

  const selectedCollectionDescription = useMemo(() => {
    const id = collectionName.trim();
    if (!id) return '';
    const c = (collectionsQuery.data?.collections ?? []).find((x) => x.id === id);
    return (c?.description ?? '').trim();
  }, [collectionName, collectionsQuery.data?.collections]);

  const selectedCollectionDisplayLabel = useMemo(() => {
    if (!hasUploadCollectionSelected) return '';
    const c = (collectionsQuery.data?.collections ?? []).find(
      (x) => x.id === collectionName.trim()
    );
    return c?.collection_display_name?.trim() || collectionName.trim();
  }, [hasUploadCollectionSelected, collectionName, collectionsQuery.data?.collections]);

  const historyCollectionOptions = useMemo(() => {
    const s = new Set<string>();
    for (const row of groupedIngestRows) {
      const v = row.collection.trim();
      if (v !== '' && v !== '—') s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
  }, [groupedIngestRows]);

  const jobStatusQueries = useQueries({
    queries: pollJobIds.map((jobId) => ({
      queryKey: ['rag-job-status', jobId] as const,
      queryFn: () => ragApi.getJob(jobId),
      enabled: pollJobIds.length > 0,
      staleTime: 0,
      refetchInterval: ragJobPollInterval,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });

  const jobScheduleById = useMemo(() => {
    const m: Record<
      string,
      { started_at?: string; finished_at?: string; status?: string; errorDetail?: string }
    > = {};
    pollJobIds.forEach((id, i) => {
      const d = jobStatusQueries[i]?.data as RagJobStatusResponse | undefined;
      if (d) {
        const errRaw = stringifyRagError(d.error);
        m[id] = {
          started_at: d.started_at,
          finished_at: d.finished_at,
          status: d.status != null && String(d.status) !== '' ? String(d.status) : undefined,
          errorDetail: errRaw !== '' ? errRaw : undefined,
        };
      }
    });
    return m;
  }, [pollJobIds, jobStatusQueries]);

  const refreshBuildHistory = useCallback(async () => {
    setBuildHistoryRefreshing(true);
    try {
      await Promise.all([jobsQuery.refetch(), rerunJobsQuery.refetch()]);
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'rag-job-status',
      });
    } finally {
      setBuildHistoryRefreshing(false);
    }
  }, [jobsQuery, rerunJobsQuery, queryClient]);

  const historyStatusOptions = useMemo(() => {
    const s = new Set<string>();
    for (const row of groupedIngestRows) {
      const jid = row.job_id;
      const sched = jid ? jobScheduleById[jid] : undefined;
      s.add(computeBuildHistoryStatusLabel(row, sched));
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'));
  }, [groupedIngestRows, jobScheduleById]);

  const filteredGroupedIngestRows = useMemo(() => {
    return groupedIngestRows.filter((row) => {
      if (historyCollectionFilter !== '' && row.collection !== historyCollectionFilter) {
        return false;
      }
      if (historyStatusFilter !== '') {
        const jid = row.job_id;
        const sched = jid ? jobScheduleById[jid] : undefined;
        if (computeBuildHistoryStatusLabel(row, sched) !== historyStatusFilter) {
          return false;
        }
      }
      return true;
    });
  }, [groupedIngestRows, historyCollectionFilter, historyStatusFilter, jobScheduleById]);

  const selectableRows = useMemo(
    () => filteredGroupedIngestRows.filter((r) => r.platformIds.length > 0),
    [filteredGroupedIngestRows]
  );
  const allSelectableSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selectedRowKeys.has(r.rowKey));
  const someRowsSelected = selectedRowKeys.size > 0;

  useEffect(() => {
    const el = headerSelectRef.current;
    if (el) {
      el.indeterminate = someRowsSelected && !allSelectableSelected;
    }
  }, [someRowsSelected, allSelectableSelected]);

  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const visible = new Set(filteredGroupedIngestRows.map((r) => r.rowKey));
      let dropped = false;
      const next = new Set<string>();
      for (const k of prev) {
        if (visible.has(k)) next.add(k);
        else dropped = true;
      }
      if (!dropped && next.size === prev.size) return prev;
      return next;
    });
  }, [filteredGroupedIngestRows]);

  const closeHistoryFilterMenu = useCallback(() => {
    setHistoryFilterMenuOpen(null);
    setHistoryFilterDropdownPos(null);
  }, []);

  useLayoutEffect(() => {
    if (!historyFilterMenuOpen) {
      setHistoryFilterDropdownPos(null);
      return;
    }
    const btn =
      historyFilterMenuOpen === 'collection'
        ? historyFilterCollectionBtnRef.current
        : historyFilterStatusBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const width = Math.max(168, r.width);
    let left = r.left;
    if (vw > 0 && left + width > vw - 8) {
      left = Math.max(8, vw - 8 - width);
    }
    setHistoryFilterDropdownPos({ top: r.bottom + 6, left, width });
  }, [historyFilterMenuOpen]);

  useEffect(() => {
    if (!historyFilterMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (historyFilterDropdownRef.current?.contains(t)) return;
      if (historyFilterCollectionBtnRef.current?.contains(t)) return;
      if (historyFilterStatusBtnRef.current?.contains(t)) return;
      closeHistoryFilterMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeHistoryFilterMenu();
    };
    const onScroll = () => closeHistoryFilterMenu();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [historyFilterMenuOpen, closeHistoryFilterMenu]);

  const deleteJobsMutation = useMutation({
    mutationFn: async (vars: { platformIds: string[]; ragJobIdsToCloseLogs: string[] }) => {
      const ids = vars.platformIds;
      if (ids.length === 0) {
        throw new Error('没有可删除的记录');
      }
      if (ids.length === 1) {
        await ragApi.deleteRagIngestJob(ids[0]);
        return { deleted: 1 };
      }
      return ragApi.deleteRagIngestJobsBatch(ids);
    },
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['rag-ingest-jobs'] });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'rag-job-status' });
      setSelectedRowKeys(new Set());
      setConfirmDelete(null);
      setLogsExpandedJobId((prev) => {
        if (prev === null) return null;
        if (vars.ragJobIdsToCloseLogs.includes(prev)) {
          return null;
        }
        return prev;
      });
    },
  });

  const rerunJobsMutation = useMutation({
    mutationFn: async (vars: {
      platformId: string;
      needsFeishu: boolean;
      ragJobIdToCloseLogs: string | null;
      name?: string;
      qdrantCollectionName?: string;
    }) => {
      const body = vars.needsFeishu
        ? {
            feishu_user_token: (feishuUserToken ?? '').trim(),
            feishu_app_id: feishuAppId?.trim() || undefined,
            feishu_app_secret: feishuAppSecret?.trim() || undefined,
            feishu_dev_space: feishuDevSpace.trim(),
            name: vars.name,
            qdrant_collection_name: vars.qdrantCollectionName,
          }
        : {
            name: vars.name,
            qdrant_collection_name: vars.qdrantCollectionName,
          };
      return ragApi.rerunRagIngestJob(vars.platformId, body);
    },
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['rag-ingest-jobs'] });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'rag-job-status' });
      setConfirmRerun(null);
      const newJobId = data.job_id?.trim();
      if (newJobId) {
        setLogsExpandedSource('history');
        setLogsExpandedJobId(newJobId);
      } else if (vars.ragJobIdToCloseLogs && logsExpandedJobId === vars.ragJobIdToCloseLogs) {
        setLogsExpandedJobId(null);
      }
      setLocalError(null);
      // 该 collection 不是蓝绿 alias，后端退回了普通导入。dirty 意味着旧向量清不掉、
      // 本次会灌出重复内容，必须明确告警而不是静默成功。
      if (data.blue_green_fallback === 'qdrant_alias_conflict_dirty') {
        toast.error(
          '该知识库未启用蓝绿模式，且上游历史记录已丢失，旧向量无法清理：本次重跑会产生重复内容，建议删除该知识库的 Qdrant collection 后重新导入',
          {
            duration: 10000,
          }
        );
      } else if (data.blue_green_fallback === 'qdrant_alias_conflict_cleaned') {
        toast.success(
          '该知识库未启用蓝绿模式，已清理旧向量后重新导入（导入期间检索结果可能短暂缺失）',
          {
            duration: 8000,
          }
        );
      }
    },
    onError: (err: unknown) => {
      setLocalError(formatRagApiError(err));
    },
  });

  const deleteRerunJobMutation = useMutation({
    mutationFn: (jobId: string) => ragApi.deleteRerunJob(jobId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rag-rerun-jobs'] });
    },
    onError: (err: unknown) => {
      setLocalError(formatRagApiError(err));
    },
  });

  const toggleRowSelected = useCallback((rowKey: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedRowKeys((prev) => {
      if (selectableRows.length === 0) return prev;
      if (selectableRows.every((r) => prev.has(r.rowKey))) {
        return new Set();
      }
      return new Set(selectableRows.map((r) => r.rowKey));
    });
  }, [selectableRows]);

  const toggleDocsRow = useCallback((rowKey: string) => {
    setDocsExpandedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const uploadMutation = useMutation({
    mutationFn: () =>
      ragApi.uploadPdfs({
        files: selectedFiles,
        qdrantCollectionName: collectionName,
      }),
    onSuccess: (data) => {
      setLogsExpandedJobId(null);
      void queryClient.invalidateQueries({ queryKey: ['rag-ingest-jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-collections'] });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'rag-job-status' });
      const anyOk = data.results.some((r) => r.ok);
      const failed = data.results.filter((r) => !r.ok);
      if (anyOk) {
        setSelectedFiles([]);
        setCollectionName('');
        queueMicrotask(() => {
          const input = fileInputRef.current;
          if (input) input.value = '';
        });
      }
      if (failed.length > 0) {
        const lines = failed.map(
          (f) => `· ${f.original_filename}：${(f.error ?? '').trim() || '未知错误'}`
        );
        setLocalError(
          failed.length === data.results.length
            ? `全部文件处理失败：\n${lines.join('\n')}`
            : `部分文件失败（已成功 ${data.results.length - failed.length} 个）：\n${lines.join('\n')}`
        );
      } else {
        setLocalError(null);
      }
      if (anyOk) {
        queueMicrotask(() => {
          document
            .getElementById('rag-build-history')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },
  });

  const feishuUploadMutation = useMutation({
    mutationFn: () =>
      ragApi.uploadFeishuDocument({
        feishuDocumentId: feishuDocumentId,
        qdrantCollectionName: collectionName,
        recursive: feishuRecursive,
        feishuUserToken: feishuUserToken ?? '',
        feishuAppId: feishuAppId,
        feishuAppSecret: feishuAppSecret,
        feishuDevSpace: feishuDevSpace,
      }),
    onSuccess: () => {
      setLogsExpandedJobId(null);
      setFeishuDocumentId('');
      setFeishuRecursive(false);
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey: ['rag-ingest-jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-collections'] });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'rag-job-status' });
      queueMicrotask(() => {
        document
          .getElementById('rag-build-history')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    onError: (err: unknown) => {
      setLocalError(err instanceof Error ? err.message : String(err));
    },
  });

  const parseWebUrls = useCallback((text: string): string[] => {
    return text
      .split(/[\n,]+/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0 && (u.startsWith('http://') || u.startsWith('https://')));
  }, []);

  const webUploadMutation = useMutation({
    mutationFn: () =>
      ragApi.uploadWebUrls({
        urls: webInputMode === 'sitemap' ? sitemapParsedUrls : parseWebUrls(webUrlsText),
        qdrantCollectionName: collectionName,
        sitemapUrl: webInputMode === 'sitemap' ? sitemapUrl : undefined,
        autoRerunIntervalHours: autoRerunIntervalHours !== 0 ? autoRerunIntervalHours : undefined,
      }),
    onSuccess: () => {
      setLogsExpandedJobId(null);
      setWebUrlsText('');
      setSitemapUrl('');
      setSitemapParsedUrls([]);
      setSitemapUnreachableCount(0);
      setAutoRerunIntervalHours(0);
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey: ['rag-ingest-jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-collections'] });
      void queryClient.invalidateQueries({ queryKey: ['rag-rerun-jobs'] });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'rag-job-status' });
      queueMicrotask(() => {
        document
          .getElementById('rag-build-history')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    onError: (err: unknown) => {
      setLocalError(err instanceof Error ? err.message : String(err));
    },
  });

  const parseSitemapMutation = useMutation({
    mutationFn: () => ragApi.parseSitemap({ url: sitemapUrl }),
    onSuccess: (data) => {
      setSitemapParsedUrls(data.urls);
      setSitemapUnreachableCount(data.unreachable ?? 0);
      setLocalError(null);
      if (data.unreachable && data.unreachable > 0) {
        setLocalError(
          `已过滤 ${data.unreachable} 个无法访问的 URL（404 或超时），实际提交 ${data.urls.length} 个`
        );
      }
      if (data.truncated && data.truncation_msg) {
        setLocalError(data.truncation_msg);
      }
    },
    onError: (err: unknown) => {
      setLocalError(err instanceof Error ? err.message : String(err));
    },
  });

  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  const lastCronIngestByJobRef = useRef<Record<string, string>>({});
  useEffect(() => {
    for (const job of activeRerunJobs) {
      const jid = job.last_ingest_job_id?.trim() ?? '';
      if (jid === '') continue;
      const prev = lastCronIngestByJobRef.current[job.id];
      if (prev !== jid) {
        lastCronIngestByJobRef.current[job.id] = jid;
        setLogsExpandedSource('cron');
        setLogsExpandedJobId(jid);
      }
    }
  }, [activeRerunJobs]);

  useEffect(() => {
    logPreRef.current?.scrollTo({ top: logPreRef.current.scrollHeight });
  }, [logText]);

  const startStream = useCallback((jobId: string, filename: string) => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    setStreamJobId(jobId);
    setStreamFileLabel(filename);
    setLogText('');
    setStreamError(null);
    setIsStreaming(true);
    const ac = new AbortController();
    streamAbortRef.current = ac;
    void ragApi
      .streamJobLogs({
        jobId,
        signal: ac.signal,
        onChunk: (t) => setLogText((prev) => normalizeStreamLogText(prev + t)),
        onError: (err) => {
          setStreamError(err.message);
        },
      })
      .finally(() => {
        if (streamAbortRef.current === ac) {
          streamAbortRef.current = null;
          setIsStreaming(false);
        }
      });
  }, []);

  /** 与「展开的任务 id」绑定：用 layout effect 与 DOM 提交对齐，避免先 paint 再订阅导致「点收起才连上流」的错觉/时序问题。 */
  useLayoutEffect(() => {
    if (logsExpandedJobId === null) {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      setIsStreaming(false);
      setStreamJobId(null);
      setLogText('');
      setStreamError(null);
      return;
    }
    const jid = logsExpandedJobId;
    const row = groupedIngestRowsRef.current.find((r) => r.job_id === jid);
    const logLabel =
      row && row.files.length > 1 ? `${row.files.length} 个文件` : (row?.files[0] ?? '日志');
    startStream(jid, logLabel);
    return () => {
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
    };
  }, [logsExpandedJobId, startStream]);

  const tryAddPdfFiles = useCallback(
    async (incoming: File[]) => {
      if (uploadMutation.isPending) {
        return;
      }
      if (pdfPageCheckLockRef.current) {
        return;
      }
      const allowed = incoming.filter(isRagIngestUploadFile);
      if (allowed.length !== incoming.length) {
        setLocalError('已忽略不支持的文件；仅支持 .pdf、.docx、.md、.markdown');
      } else {
        setLocalError(null);
      }

      const prev = selectedFilesRef.current;
      const toAdd: File[] = [];
      for (const f of allowed) {
        if (prev.length + toAdd.length >= ragApi.maxPdfs) {
          break;
        }
        const dupInPrev = prev.some((x) => x.name === f.name && x.size === f.size);
        const dupInToAdd = toAdd.some((x) => x.name === f.name && x.size === f.size);
        if (!dupInPrev && !dupInToAdd) {
          toAdd.push(f);
        }
      }

      if (toAdd.length === 0) {
        if (allowed.length > 0 && prev.length >= ragApi.maxPdfs) {
          setLocalError(`最多添加 ${ragApi.maxPdfs} 个文件`);
        }
        return;
      }

      pdfPageCheckLockRef.current = true;
      setIsCheckingPdfPages(true);
      try {
        for (const f of toAdd) {
          if (!isPdfRagUploadFile(f)) {
            continue;
          }
          const pages = await countPdfPages(f);
          if (pages > ragApi.maxPdfPages) {
            setLocalError(
              `「${f.name}」共 ${pages} 页，超过上限 ${ragApi.maxPdfPages} 页，无法添加`
            );
            return;
          }
        }

        setSelectedFiles((prevNow) => {
          const next = [...prevNow];
          for (const f of toAdd) {
            if (next.length >= ragApi.maxPdfs) {
              break;
            }
            if (!next.some((x) => x.name === f.name && x.size === f.size)) {
              next.push(f);
            }
          }
          const overCapHint =
            prevNow.length + allowed.length > ragApi.maxPdfs && next.length >= ragApi.maxPdfs;
          if (overCapHint) {
            queueMicrotask(() => setLocalError(`最多添加 ${ragApi.maxPdfs} 个文件`));
          }
          return next;
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setLocalError(`校验 PDF 页数失败：${msg}。请确认文件未损坏且为有效 PDF`);
      } finally {
        pdfPageCheckLockRef.current = false;
        setIsCheckingPdfPages(false);
      }
    },
    [uploadMutation.isPending]
  );

  const onFilesChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (!list?.length) {
        return;
      }
      await tryAddPdfFiles(Array.from(list));
      e.target.value = '';
    },
    [tryAddPdfFiles]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length) {
        void tryAddPdfFiles(files);
      }
    },
    [tryAddPdfFiles]
  );

  const removeFileAt = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const closeFilePreview = useCallback(() => {
    filePreviewRequestIdRef.current += 1;
    setFilePreviewTarget(null);
    setFilePreviewResult(null);
    setFilePreviewError(null);
    setFilePreviewLoading(false);
  }, []);

  const openFilePreview = useCallback((file: File) => {
    const requestId = filePreviewRequestIdRef.current + 1;
    filePreviewRequestIdRef.current = requestId;
    setFilePreviewTarget(file);
    setFilePreviewResult(null);
    setFilePreviewError(null);
    setFilePreviewLoading(true);
    void previewRagUploadFileFirstPage(file)
      .then((result) => {
        if (filePreviewRequestIdRef.current !== requestId) return;
        setFilePreviewResult(result);
      })
      .catch((err: unknown) => {
        if (filePreviewRequestIdRef.current !== requestId) return;
        setFilePreviewError(err instanceof Error ? err.message : '预览失败');
      })
      .finally(() => {
        if (filePreviewRequestIdRef.current !== requestId) return;
        setFilePreviewLoading(false);
      });
  }, []);

  const canSubmit = useMemo(() => {
    const name = collectionName.trim();
    if (uploadMode === 'feishu') {
      return (
        name.length > 0 &&
        feishuDocumentId.trim().length > 0 &&
        isFeishuLoggedIn &&
        !feishuUploadMutation.isPending &&
        !deleteCollectionsBatchMutation.isPending
      );
    }
    if (uploadMode === 'web') {
      const hasUrls =
        webInputMode === 'sitemap'
          ? sitemapParsedUrls.length > 0
          : parseWebUrls(webUrlsText).length > 0;
      return (
        name.length > 0 &&
        hasUrls &&
        !webUploadMutation.isPending &&
        !deleteCollectionsBatchMutation.isPending
      );
    }
    return (
      name.length > 0 &&
      selectedFiles.length > 0 &&
      !uploadMutation.isPending &&
      !deleteCollectionsBatchMutation.isPending &&
      !isCheckingPdfPages
    );
  }, [
    uploadMode,
    collectionName,
    selectedFiles.length,
    feishuDocumentId,
    isFeishuLoggedIn,
    webUrlsText,
    sitemapParsedUrls,
    webInputMode,
    parseWebUrls,
    uploadMutation.isPending,
    feishuUploadMutation.isPending,
    webUploadMutation.isPending,
    deleteCollectionsBatchMutation.isPending,
    isCheckingPdfPages,
  ]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      const name = collectionName.trim();
      if (!name) {
        setLocalError('请先新建或选择 Qdrant collection');
        return;
      }
      if (uploadMode === 'feishu') {
        const docId = feishuDocumentId.trim();
        if (!docId) {
          setLocalError('请输入飞书文档 ID');
          return;
        }
        feishuUploadMutation.mutate();
        return;
      }
      if (uploadMode === 'web') {
        const urls = webInputMode === 'sitemap' ? sitemapParsedUrls : parseWebUrls(webUrlsText);
        if (urls.length === 0) {
          if (webInputMode === 'sitemap') {
            setLocalError('请先解析 sitemap 以获取 URL 列表');
          } else {
            setLocalError('请输入至少一个有效的网页 URL（须以 http:// 或 https:// 开头）');
          }
          return;
        }
        webUploadMutation.mutate();
        return;
      }
      if (selectedFiles.length === 0) {
        setLocalError('请添加至少一个文档文件（.pdf / .docx / .md）');
        return;
      }
      if (pdfPageCheckLockRef.current) {
        return;
      }
      pdfPageCheckLockRef.current = true;
      setIsCheckingPdfPages(true);
      try {
        for (const f of selectedFiles) {
          if (!isPdfRagUploadFile(f)) {
            continue;
          }
          const pages = await countPdfPages(f);
          if (pages > ragApi.maxPdfPages) {
            setLocalError(
              `「${f.name}」共 ${pages} 页，超过上限 ${ragApi.maxPdfPages} 页，请移除后重试`
            );
            return;
          }
        }
        uploadMutation.mutate();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setLocalError(`校验 PDF 页数失败：${msg}。请确认文件未损坏且为有效 PDF`);
      } finally {
        pdfPageCheckLockRef.current = false;
        setIsCheckingPdfPages(false);
      }
    },
    [
      collectionName,
      uploadMode,
      selectedFiles,
      feishuDocumentId,
      webUrlsText,
      sitemapParsedUrls,
      webInputMode,
      parseWebUrls,
      uploadMutation,
      feishuUploadMutation,
      webUploadMutation,
    ]
  );

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">知识库管理</h1>
            <p className="text-sm text-muted-foreground mt-1">
              知识库的文档侧：管理 Qdrant Collection（可选、用于上传与智能体插件）、上传 PDF / DOCX
              / Markdown 写入向量，并在本页查看构建历史与流式日志。
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-r from-emerald-500/5 via-background to-background p-5 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-default">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-500 to-teal-400 rounded-l-xl" />
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 shrink-0 group-hover:scale-110 transition-transform duration-300">
              <BookStackIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground mb-1">文档构建知识库</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                系统将逐个处理上传的文件：解析正文与版面、分块、embedding。支持
                PDF、DOCX、Markdown（.md / .markdown）。
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  单次最多 {ragApi.maxPdfs} 个文件
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                  可新建或选择已有 collection
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  同页下方可查看构建历史、日志与批量删除
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
          <div className="p-5 space-y-8 border-t border-transparent">
            <section id="rag-collections-section" className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-start gap-2 rounded-md text-left outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setMyCollectionsSectionExpanded((v) => !v)}
                  aria-expanded={myCollectionsSectionExpanded}
                  aria-controls="rag-my-collections-panel"
                  id="rag-my-collections-heading"
                >
                  <ChevronRightIcon
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                      myCollectionsSectionExpanded && 'rotate-90'
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-foreground">我的 Collection</h2>
                    {myCollectionsSectionExpanded ? (
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        以下为可勾选、用于批量删除或「用于上传」的向量集合；在智能体插件中绑定 RAG
                        时也使用这些 collection。
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        {collectionsQuery.isLoading
                          ? '加载中…'
                          : collectionRows.length === 0
                            ? '暂无 collection'
                            : `共 ${collectionRows.length} 个，点击展开管理`}
                      </p>
                    )}
                  </div>
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-7 px-2 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  disabled={
                    selectedCollectionIds.size === 0 ||
                    collectionsQuery.isLoading ||
                    createCollectionMutation.isPending ||
                    deleteCollectionsBatchMutation.isPending
                  }
                  onClick={() => {
                    const rows = collectionRows.filter((c) => selectedCollectionIds.has(c.id));
                    if (rows.length === 0) return;
                    setLocalError(null);
                    deleteCollectionsBatchMutation.reset();
                    setBatchDeleteCollectionsDialog(rows);
                  }}
                >
                  <TrashIcon className="h-3 w-3 shrink-0" />
                  批量删除
                  {selectedCollectionIds.size > 0 ? ` (${selectedCollectionIds.size})` : ''}
                </Button>
              </div>
              {myCollectionsSectionExpanded ? (
                <div
                  id="rag-my-collections-panel"
                  role="region"
                  aria-labelledby="rag-my-collections-heading"
                >
                  {collectionsQuery.isLoading ? (
                    <Skeleton className="h-28 w-full rounded-lg" />
                  ) : collectionRows.length === 0 ? (
                    <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border/80 bg-muted/10 px-4 py-8 text-center">
                      暂无 collection，可使用下方「新建 collection」创建后再上传文档。
                    </p>
                  ) : (
                    <div className="rounded-lg border border-border/80 overflow-x-auto [&_th]:px-3 [&_td]:px-3 [&_th]:h-10 [&_td]:py-2.5">
                      <Table className="min-w-[880px]">
                        <TableHeader>
                          <TableRow className="hover:bg-transparent bg-muted/40 border-border/60">
                            <TableHead className="w-10 pr-0">
                              <input
                                ref={collectionListHeaderSelectRef}
                                type="checkbox"
                                className="h-4 w-4 rounded border-input align-middle"
                                checked={allCollectionsSelected}
                                disabled={
                                  collectionRows.length === 0 ||
                                  deleteCollectionsBatchMutation.isPending
                                }
                                onChange={toggleSelectAllCollections}
                                aria-label="全选 collection"
                              />
                            </TableHead>
                            <TableHead className="font-medium whitespace-nowrap">名称</TableHead>
                            <TableHead className="font-medium min-w-[10rem]">说明</TableHead>
                            <TableHead className="font-medium whitespace-nowrap">
                              向量模型（只读）
                            </TableHead>
                            <TableHead className="font-medium whitespace-nowrap">
                              向量维度
                            </TableHead>
                            <TableHead className="font-medium whitespace-nowrap">
                              chunk_overlap
                            </TableHead>
                            <TableHead className="font-medium whitespace-nowrap">
                              distance
                            </TableHead>
                            <TableHead className="font-medium min-w-[14rem] w-[1%] text-right whitespace-nowrap">
                              操作
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {collectionRows.map((row) => {
                            const name = row.collection_display_name?.trim() || '—';
                            const desc = row.description?.trim();
                            const isActiveForUpload =
                              collectionName.trim() !== '' &&
                              collectionName.trim() === row.id.trim();
                            return (
                              <TableRow
                                key={row.id}
                                className={cn(
                                  'border-border/60',
                                  isActiveForUpload &&
                                    'bg-primary/10 border-l-2 border-l-primary shadow-sm'
                                )}
                              >
                                <TableCell className="w-10 align-top pr-0 pt-2.5">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-input"
                                    checked={selectedCollectionIds.has(row.id)}
                                    disabled={deleteCollectionsBatchMutation.isPending}
                                    onChange={() => toggleCollectionRowSelected(row.id)}
                                    aria-label={`选择 ${name}`}
                                  />
                                </TableCell>
                                <TableCell className="align-top max-w-[14rem] min-w-0">
                                  <p
                                    className={cn(
                                      'text-sm font-medium break-words',
                                      isActiveForUpload ? 'text-primary' : 'text-foreground'
                                    )}
                                  >
                                    {name}
                                  </p>
                                </TableCell>
                                <TableCell className="align-top text-sm text-muted-foreground min-w-0 max-w-xl">
                                  {desc ? (
                                    <DescriptionClampExpand
                                      text={desc}
                                      textClassName="text-sm text-muted-foreground"
                                    />
                                  ) : (
                                    <span className="text-xs">—</span>
                                  )}
                                </TableCell>
                                <TableCell className="align-top text-sm text-muted-foreground whitespace-nowrap">
                                  {row.embeddingProfileId || '暂未获取'}
                                </TableCell>
                                <TableCell className="align-top text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                                  {collectionVectorSizeLabel(row)}
                                </TableCell>
                                <TableCell className="align-top text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                                  {collectionChunkOverlapLabel(row)}
                                </TableCell>
                                <TableCell className="align-top text-sm text-muted-foreground whitespace-nowrap">
                                  {collectionDistanceLabel(row)}
                                </TableCell>
                                <TableCell className="align-top text-right min-w-[14rem] whitespace-nowrap">
                                  <div className="flex flex-row flex-wrap items-center justify-end gap-1.5">
                                    {isActiveForUpload ? (
                                      <Badge
                                        variant="secondary"
                                        className="shrink-0 text-[10px] font-normal px-1.5 py-0 h-5"
                                      >
                                        当前用于上传
                                      </Badge>
                                    ) : null}
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 px-2 text-xs shrink-0"
                                      disabled={
                                        collectionsQuery.isLoading ||
                                        createCollectionMutation.isPending ||
                                        deleteCollectionsBatchMutation.isPending
                                      }
                                      onClick={() => {
                                        setCollectionName(row.id);
                                        setLocalError(null);
                                        setMyCollectionsSectionExpanded(false);
                                      }}
                                    >
                                      用于上传
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : null}
            </section>

            <form id="rag-upload-section" onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label
                    htmlFor="qdrant_collection_name"
                    className="text-sm font-medium text-foreground"
                  >
                    选择 Collection
                    {hasUploadCollectionSelected ? (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        · {selectedCollectionDisplayLabel}
                      </span>
                    ) : null}
                  </Label>
                  {uploadMode === 'file' ? (
                    <Badge
                      variant={selectedFiles.length > 0 ? 'default' : 'secondary'}
                      className={cn(
                        'font-normal shrink-0',
                        hasUploadCollectionSelected &&
                          selectedFiles.length === 0 &&
                          'border-primary/40 bg-primary/10 text-primary'
                      )}
                    >
                      已选 {selectedFiles.length} / {ragApi.maxPdfs} 个文件
                    </Badge>
                  ) : null}
                </div>
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                  <div className="-ml-1 w-full min-w-0 sm:w-[14rem] sm:flex-none">
                    <Select
                      value={collectionName}
                      onValueChange={(value) => {
                        setCollectionName(value);
                        setLocalError(null);
                      }}
                    >
                      <div className="relative w-full">
                        <SelectTrigger
                          id="qdrant_collection_name"
                          disabled={
                            collectionsQuery.isLoading ||
                            createCollectionMutation.isPending ||
                            deleteCollectionsBatchMutation.isPending
                          }
                          className={cn(
                            'h-10 w-full border-input bg-muted/30 font-normal shadow-none focus:bg-background focus:ring-2 focus:ring-ring focus:ring-offset-2',
                            hasUploadCollectionSelected &&
                              '[&>span:first-child]:min-w-0 [&>span:first-child]:pr-7'
                          )}
                        >
                          <SelectValue
                            placeholder={
                              collectionsQuery.isLoading ? '加载中…' : '请选择 collection'
                            }
                          >
                            {hasUploadCollectionSelected ? (
                              <span
                                className="block truncate"
                                title={selectedCollectionDisplayLabel}
                              >
                                {selectedCollectionDisplayLabel}
                              </span>
                            ) : null}
                          </SelectValue>
                        </SelectTrigger>
                        {hasUploadCollectionSelected ? (
                          <button
                            type="button"
                            className="absolute right-9 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-xs leading-none text-muted-foreground/80 hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-60"
                            disabled={
                              collectionsQuery.isLoading ||
                              createCollectionMutation.isPending ||
                              deleteCollectionsBatchMutation.isPending
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCollectionName('');
                              setLocalError(null);
                            }}
                            aria-label="取消选择 collection"
                          >
                            <span aria-hidden>×</span>
                          </button>
                        ) : null}
                      </div>
                      <SelectContent className="max-h-60">
                        {(collectionsQuery.data?.collections ?? []).map((row) => (
                          <SelectItem key={row.id} value={row.id} className="max-w-[20rem]">
                            <span className="truncate">
                              {row.collection_display_name || row.id}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex min-w-0 w-full flex-col gap-2 sm:flex-1 sm:min-w-0">
                    {newCollectionInputOpen ? (
                      <>
                        <div className="flex min-w-0 flex-wrap items-center justify-start gap-2">
                          <Input
                            ref={newCollectionInputRef}
                            id="collection_display_name"
                            value={newCollectionDisplayName}
                            onChange={(e) => setNewCollectionDisplayName(e.target.value)}
                            placeholder="新建 collection"
                            disabled={
                              createCollectionMutation.isPending ||
                              deleteCollectionsBatchMutation.isPending
                            }
                            className="w-full max-w-[8.4rem] flex-none bg-muted/30 focus-visible:bg-background sm:w-[8.4rem]"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            className="shrink-0 text-muted-foreground"
                            disabled={
                              createCollectionMutation.isPending ||
                              deleteCollectionsBatchMutation.isPending
                            }
                            onClick={() => {
                              setNewCollectionInputOpen(false);
                              setNewCollectionDisplayName('');
                              setNewCollectionDescription('');
                              setNewCollectionVectorSize('');
                              setNewCollectionChunkOverlap('');
                              setNewCollectionDistance('');
                            }}
                          >
                            取消
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="shrink-0"
                            disabled={
                              createCollectionMutation.isPending ||
                              deleteCollectionsBatchMutation.isPending ||
                              newCollectionDisplayName.trim().length === 0
                            }
                            onClick={() => {
                              const vectorSizeRaw = newCollectionVectorSize.trim();
                              const chunkOverlapRaw = newCollectionChunkOverlap.trim();
                              const distanceRaw = newCollectionDistance.trim();
                              let vectorSize: number | undefined;
                              let chunkOverlap: number | undefined;
                              let distance: string | undefined;
                              if (vectorSizeRaw !== '') {
                                const n = Number(vectorSizeRaw);
                                if (
                                  !RAG_COLLECTION_VECTOR_SIZE_OPTIONS.includes(
                                    n as (typeof RAG_COLLECTION_VECTOR_SIZE_OPTIONS)[number]
                                  )
                                ) {
                                  setLocalError('vector_size 须为 512、1024、2048 或 2560');
                                  return;
                                }
                                vectorSize = n;
                              }
                              if (chunkOverlapRaw !== '') {
                                const n = Number(chunkOverlapRaw);
                                if (
                                  !RAG_COLLECTION_CHUNK_OVERLAP_OPTIONS.includes(
                                    n as (typeof RAG_COLLECTION_CHUNK_OVERLAP_OPTIONS)[number]
                                  )
                                ) {
                                  setLocalError('chunk_overlap 须为 100、200 或 400');
                                  return;
                                }
                                chunkOverlap = n;
                              }
                              if (distanceRaw !== '') {
                                if (
                                  !RAG_COLLECTION_DISTANCE_OPTIONS.includes(
                                    distanceRaw as (typeof RAG_COLLECTION_DISTANCE_OPTIONS)[number]
                                  )
                                ) {
                                  setLocalError('distance 须为 cosine、euclid 或 dot');
                                  return;
                                }
                                distance = distanceRaw;
                              }
                              setLocalError(null);
                              createCollectionMutation.mutate({
                                displayName: newCollectionDisplayName.trim(),
                                description: newCollectionDescription,
                                vectorSize,
                                chunkOverlap,
                                distance,
                              });
                            }}
                          >
                            {createCollectionMutation.isPending ? (
                              <>
                                <LoaderIcon className="h-4 w-4 animate-spin mr-2" />
                                创建中…
                              </>
                            ) : (
                              '确认创建'
                            )}
                          </Button>
                        </div>
                        <div className="w-full min-w-0 space-y-1">
                          <Label
                            htmlFor="collection_description"
                            className="text-xs font-normal text-muted-foreground"
                          >
                            说明（可选，最多 2000 字）
                          </Label>
                          <textarea
                            id="collection_description"
                            value={newCollectionDescription}
                            onChange={(e) => setNewCollectionDescription(e.target.value)}
                            maxLength={2000}
                            rows={3}
                            disabled={
                              createCollectionMutation.isPending ||
                              deleteCollectionsBatchMutation.isPending
                            }
                            placeholder="简要描述该知识库的用途或内容范围…"
                            className="flex w-full min-w-0 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-60 resize-y min-h-[4.5rem]"
                          />
                        </div>
                        <div className="grid w-full min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <div className="space-y-1">
                            <Label
                              htmlFor="collection_vector_size"
                              className="text-xs font-normal text-muted-foreground"
                            >
                              向量维度（可选）
                            </Label>
                            <select
                              id="collection_vector_size"
                              value={newCollectionVectorSize}
                              onChange={(e) => setNewCollectionVectorSize(e.target.value)}
                              disabled={
                                createCollectionMutation.isPending ||
                                deleteCollectionsBatchMutation.isPending
                              }
                              className="h-10 w-full min-w-0 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <option value="">
                                {RAG_COLLECTION_DEFAULT_VECTOR_SIZE}（default）
                              </option>
                              {RAG_COLLECTION_VECTOR_SIZE_OPTIONS.filter(
                                (size) => size !== RAG_COLLECTION_DEFAULT_VECTOR_SIZE
                              ).map((size) => (
                                <option key={size} value={String(size)}>
                                  {size}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor="collection_chunk_overlap"
                              className="text-xs font-normal text-muted-foreground"
                            >
                              chunk_overlap（可选）
                            </Label>
                            <select
                              id="collection_chunk_overlap"
                              value={newCollectionChunkOverlap}
                              onChange={(e) => setNewCollectionChunkOverlap(e.target.value)}
                              disabled={
                                createCollectionMutation.isPending ||
                                deleteCollectionsBatchMutation.isPending
                              }
                              className="h-10 w-full min-w-0 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <option value="">
                                {RAG_COLLECTION_DEFAULT_CHUNK_OVERLAP}（default）
                              </option>
                              {RAG_COLLECTION_CHUNK_OVERLAP_OPTIONS.filter(
                                (overlap) => overlap !== RAG_COLLECTION_DEFAULT_CHUNK_OVERLAP
                              ).map((overlap) => (
                                <option key={overlap} value={String(overlap)}>
                                  {overlap}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor="collection_distance"
                              className="text-xs font-normal text-muted-foreground"
                            >
                              distance（可选）
                            </Label>
                            <select
                              id="collection_distance"
                              value={newCollectionDistance}
                              onChange={(e) => setNewCollectionDistance(e.target.value)}
                              disabled={
                                createCollectionMutation.isPending ||
                                deleteCollectionsBatchMutation.isPending
                              }
                              className="h-10 w-full min-w-0 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <option value="">{RAG_COLLECTION_DEFAULT_DISTANCE}（default）</option>
                              {RAG_COLLECTION_DISTANCE_OPTIONS.filter(
                                (metric) => metric !== RAG_COLLECTION_DEFAULT_DISTANCE
                              ).map((metric) => (
                                <option key={metric} value={metric}>
                                  {metric}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 w-fit shrink-0 self-start"
                        disabled={
                          collectionsQuery.isLoading ||
                          createCollectionMutation.isPending ||
                          deleteCollectionsBatchMutation.isPending
                        }
                        onClick={() => setNewCollectionInputOpen(true)}
                      >
                        新建 collection
                      </Button>
                    )}
                  </div>
                </div>
                {selectedCollectionDescription ? (
                  <DescriptionClampExpand
                    text={selectedCollectionDescription}
                    className="max-w-3xl"
                    textClassName="text-xs text-muted-foreground"
                  />
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-lg border border-border/80 bg-muted/30 p-0.5">
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        uploadMode === 'file'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      onClick={() => setUploadMode('file')}
                    >
                      <UploadCloudIcon className="h-4 w-4" />
                      上传文件
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        uploadMode === 'feishu'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      onClick={() => setUploadMode('feishu')}
                    >
                      <FeishuIcon className="h-4 w-4" />
                      飞书文档
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        uploadMode === 'web'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      onClick={() => setUploadMode('web')}
                    >
                      <GlobeIcon className="h-4 w-4" />
                      网页爬取
                    </button>
                  </div>
                </div>
              </div>

              {uploadMode === 'file' ? (
                <>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-sm font-medium">文档文件</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        支持 .pdf、.docx、.md、.markdown；其中 PDF 单个不得超过 {ragApi.maxPdfPages}{' '}
                        页
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown"
                      multiple
                      className="sr-only"
                      id="rag_ingest_files"
                      disabled={uploadMutation.isPending || isCheckingPdfPages}
                      onChange={onFilesChange}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(ev) => {
                        if (uploadMutation.isPending || isCheckingPdfPages) {
                          return;
                        }
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      onClick={() => {
                        if (uploadMutation.isPending || isCheckingPdfPages) {
                          return;
                        }
                        fileInputRef.current?.click();
                      }}
                      onDragEnter={(e) => {
                        if (uploadMutation.isPending || isCheckingPdfPages) {
                          return;
                        }
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        if (!e.currentTarget.contains(e.relatedTarget as Node))
                          setDragActive(false);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={onDrop}
                      className={cn(
                        'rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
                        uploadMutation.isPending || isCheckingPdfPages
                          ? 'cursor-not-allowed opacity-60 border-muted-foreground/20 bg-muted/10'
                          : 'cursor-pointer',
                        !uploadMutation.isPending &&
                          !isCheckingPdfPages &&
                          (dragActive
                            ? 'border-primary bg-primary/5'
                            : 'border-muted-foreground/25 bg-muted/20 hover:border-primary/40 hover:bg-muted/30')
                      )}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                          <UploadCloudIcon className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          拖拽文档到此处，或点击选择文件
                        </p>
                        <p className="text-xs text-muted-foreground">
                          .pdf / .docx / .md / .markdown，单次最多 {ragApi.maxPdfs} 个
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="mt-2"
                          disabled={uploadMutation.isPending || isCheckingPdfPages}
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                        >
                          选择文件
                        </Button>
                      </div>
                    </div>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="rounded-lg border bg-muted/10 divide-y max-h-52 overflow-y-auto">
                      {selectedFiles.map((f, index) => (
                        <div
                          key={`${f.name}-${f.size}-${index}`}
                          className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                        >
                          <div className="min-w-0 flex-1 flex items-center gap-2">
                            <p className="font-medium text-foreground truncate">{f.name}</p>
                            <p className="text-xs text-muted-foreground shrink-0">
                              {formatBytes(f.size)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-muted-foreground hover:text-foreground"
                              disabled={uploadMutation.isPending || isCheckingPdfPages}
                              onClick={(e) => {
                                e.stopPropagation();
                                openFilePreview(f);
                              }}
                            >
                              预览
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive"
                              disabled={uploadMutation.isPending || isCheckingPdfPages}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFileAt(index);
                              }}
                              aria-label="移除文件"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(localError || uploadMutation.isError) && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                      {localError ??
                        (uploadMutation.error instanceof Error
                          ? uploadMutation.error.message
                          : '生成失败')}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedFiles([]);
                        setLocalError(null);
                      }}
                      disabled={
                        uploadMutation.isPending || isCheckingPdfPages || selectedFiles.length === 0
                      }
                    >
                      清空列表
                    </Button>
                    <Button type="submit" disabled={!canSubmit} className="min-w-[140px] gap-2">
                      {uploadMutation.isPending ? (
                        <>
                          <LoaderIcon className="h-4 w-4 animate-spin" />
                          生成中…
                        </>
                      ) : isCheckingPdfPages ? (
                        <>
                          <LoaderIcon className="h-4 w-4 animate-spin" />
                          校验中…
                        </>
                      ) : (
                        '生成 RAG 知识库'
                      )}
                    </Button>
                  </div>
                </>
              ) : uploadMode === 'feishu' ? (
                <>
                  {/* --- Feishu app credential configuration --- */}
                  <div className="space-y-2 rounded-lg border p-3 bg-muted/5">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
                        <span className="text-sm font-medium leading-none">飞书应用配置</span>
                        <FeishuRAGPermissionRecommendations />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          'shrink-0 h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-destructive',
                          !isFeishuReady && 'invisible pointer-events-none'
                        )}
                        disabled={!isFeishuReady}
                        tabIndex={isFeishuReady ? 0 : -1}
                        aria-hidden={!isFeishuReady}
                        onClick={clearFeishuConfig}
                      >
                        清除配置
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      请填写飞书开放平台的应用凭证。回调地址需在飞书应用后台注册：
                      <code className="ml-1 px-1 py-0.5 rounded bg-muted text-xs">
                        {feishuRedirectUri}
                      </code>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="feishu_app_id" className="text-xs">
                          应用 ID
                        </Label>
                        <Input
                          id="feishu_app_id"
                          value={feishuAppId}
                          onChange={(e) =>
                            setFeishuAppConfig(e.target.value, feishuAppSecret, feishuDevSpace)
                          }
                          placeholder="cli_xxxxxxxx"
                          disabled={feishuUploadMutation.isPending}
                          className="h-8 text-xs bg-muted/30 focus-visible:bg-background"
                        />
                      </div>
                      <div>
                        <Label htmlFor="feishu_app_secret" className="text-xs">
                          应用密钥
                        </Label>
                        <Input
                          id="feishu_app_secret"
                          type="password"
                          value={feishuAppSecret}
                          onChange={(e) =>
                            setFeishuAppConfig(feishuAppId, e.target.value, feishuDevSpace)
                          }
                          placeholder="应用 Secret"
                          disabled={feishuUploadMutation.isPending}
                          className="h-8 text-xs bg-muted/30 focus-visible:bg-background"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="feishu_dev_space" className="text-xs">
                        知识空间 ID
                      </Label>
                      <Input
                        id="feishu_dev_space"
                        value={feishuDevSpace}
                        onChange={(e) =>
                          setFeishuAppConfig(feishuAppId, feishuAppSecret, e.target.value)
                        }
                        placeholder="飞书知识空间 ID"
                        disabled={feishuUploadMutation.isPending}
                        className="h-8 text-xs bg-muted/30 focus-visible:bg-background"
                      />
                    </div>
                  </div>

                  {/* --- Feishu OAuth login status --- */}
                  <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FeishuIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {isFeishuLoggedIn ? (
                        <>
                          <span className="text-sm text-emerald-700 font-medium">已登录飞书</span>
                          <span className="text-xs text-muted-foreground">
                            （Token 已保存；过期后需重新登录）
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-amber-700">
                          未登录飞书，需先填写应用配置并授权
                        </span>
                      )}
                    </div>
                    {isFeishuLoggedIn ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 h-7 px-2 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={clearFeishuToken}
                      >
                        退出飞书
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="shrink-0 h-7 px-2 text-xs gap-1"
                        disabled={!isFeishuReady || !token}
                        onClick={async () => {
                          if (!token || !feishuAppId || !feishuAppSecret) return;
                          try {
                            const resp = await ragApi.initiateFeishuAuth({
                              token,
                              appId: feishuAppId,
                              appSecret: feishuAppSecret,
                              redirectUri: feishuRedirectUri,
                            });
                            if (resp.authorize_url) {
                              window.location.href = resp.authorize_url;
                            }
                          } catch (err) {
                            setLocalError(err instanceof Error ? err.message : '飞书授权请求失败');
                          }
                        }}
                      >
                        飞书登录
                      </Button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="feishu_document_id" className="text-sm font-medium">
                        飞书文档 ID
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        飞书 Wiki 页面 token 或文档 obj_token（在飞书文档 URL 中获取）
                      </p>
                    </div>
                    <Input
                      id="feishu_document_id"
                      value={feishuDocumentId}
                      onChange={(e) => setFeishuDocumentId(e.target.value)}
                      placeholder="例如：V7hmwNPZEinzcakiTyXcDxyfnCh"
                      disabled={feishuUploadMutation.isPending}
                      className="bg-muted/30 focus-visible:bg-background"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="feishu_recursive"
                      type="checkbox"
                      className="h-4 w-4 rounded border-input"
                      checked={feishuRecursive}
                      onChange={(e) => setFeishuRecursive(e.target.checked)}
                      disabled={feishuUploadMutation.isPending}
                    />
                    <Label htmlFor="feishu_recursive" className="text-sm text-muted-foreground">
                      递归爬取子文档（知识库页面含子页面时可选）
                    </Label>
                  </div>

                  {(localError || feishuUploadMutation.isError) && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                      {localError ??
                        (feishuUploadMutation.error instanceof Error
                          ? feishuUploadMutation.error.message
                          : '摄入失败')}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFeishuDocumentId('');
                        setFeishuRecursive(false);
                        setLocalError(null);
                      }}
                      disabled={
                        feishuUploadMutation.isPending || feishuDocumentId.trim().length === 0
                      }
                    >
                      清空输入
                    </Button>
                    <Button type="submit" disabled={!canSubmit} className="min-w-[140px] gap-2">
                      {feishuUploadMutation.isPending ? (
                        <>
                          <LoaderIcon className="h-4 w-4 animate-spin" />
                          提交中…
                        </>
                      ) : (
                        '摄入飞书文档'
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* --- URL source toggle --- */}
                  <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-lg border border-border/80 bg-muted/30 p-0.5">
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                          webInputMode === 'sitemap'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                        onClick={() => {
                          setWebInputMode('sitemap');
                          setSitemapParsedUrls([]);
                          setSitemapUnreachableCount(0);
                          setWebUrlsText('');
                        }}
                        disabled={webUploadMutation.isPending}
                      >
                        <GlobeIcon className="h-4 w-4" />
                        Sitemap 解析
                      </button>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                          webInputMode === 'manual'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                        onClick={() => {
                          setWebInputMode('manual');
                          setSitemapUrl('');
                          setSitemapParsedUrls([]);
                          setSitemapUnreachableCount(0);
                        }}
                        disabled={webUploadMutation.isPending}
                      >
                        <NotesHintIcon className="h-4 w-4" />
                        手动输入
                      </button>
                    </div>
                  </div>

                  {webInputMode === 'sitemap' ? (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-sm font-medium">Sitemap URL</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          输入 sitemap.xml 地址，解析后自动提取所有页面 URL
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={sitemapUrl}
                          onChange={(e) => {
                            setSitemapUrl(e.target.value);
                            if (sitemapParsedUrls.length > 0) {
                              setSitemapParsedUrls([]);
                              setSitemapUnreachableCount(0);
                            }
                          }}
                          placeholder="https://docs.infini-ai.com/sitemap.xml"
                          disabled={parseSitemapMutation.isPending || webUploadMutation.isPending}
                          className="bg-muted/30 focus-visible:bg-background"
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="shrink-0 h-9 px-3 gap-1"
                          disabled={
                            parseSitemapMutation.isPending ||
                            webUploadMutation.isPending ||
                            !sitemapUrl.trim() ||
                            (!sitemapUrl.trim().startsWith('http://') &&
                              !sitemapUrl.trim().startsWith('https://'))
                          }
                          onClick={() => {
                            setLocalError(null);
                            parseSitemapMutation.mutate();
                          }}
                        >
                          {parseSitemapMutation.isPending ? (
                            <>
                              <LoaderIcon className="h-4 w-4 animate-spin" />
                              解析中…
                            </>
                          ) : (
                            '解析'
                          )}
                        </Button>
                      </div>
                      {parseSitemapMutation.isError && (
                        <p className="text-xs text-destructive">
                          {parseSitemapMutation.error instanceof Error
                            ? parseSitemapMutation.error.message
                            : '解析失败'}
                        </p>
                      )}
                      {sitemapParsedUrls.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-700 font-medium">
                              已提取 {sitemapParsedUrls.length} 个有效 URL
                            </span>
                            {sitemapUnreachableCount > 0 && (
                              <span className="text-xs text-amber-600">
                                （已过滤 {sitemapUnreachableCount} 个无法访问的 URL）
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="shrink-0 h-6 px-2 text-xs text-muted-foreground hover:text-destructive"
                              onClick={() => {
                                setSitemapParsedUrls([]);
                                setSitemapUnreachableCount(0);
                                setSitemapUrl('');
                              }}
                              disabled={webUploadMutation.isPending}
                            >
                              清除
                            </Button>
                          </div>
                          <div className="rounded-md border border-border/50 bg-background/80 max-h-[120px] overflow-y-auto px-2 py-1.5 text-xs text-muted-foreground">
                            {sitemapParsedUrls.slice(0, 20).map((u, i) => (
                              <div key={i} className="truncate py-0.5">
                                {u}
                              </div>
                            ))}
                            {sitemapParsedUrls.length > 20 && (
                              <div className="py-0.5 text-muted-foreground/60">
                                …还有 {sitemapParsedUrls.length - 20} 个 URL
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-sm font-medium">网页 URL</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          输入需要爬取的网页地址，每行一个 URL（须以 http:// 或 https:// 开头）
                        </p>
                      </div>
                      <textarea
                        value={webUrlsText}
                        onChange={(e) => setWebUrlsText(e.target.value)}
                        placeholder="https://docs.example.com/guide.html&#10;https://blog.example.com/article"
                        disabled={webUploadMutation.isPending}
                        rows={5}
                        className="w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background disabled:cursor-not-allowed disabled:opacity-60 resize-y min-h-[100px]"
                      />
                      {webUrlsText.trim().length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          已识别 {parseWebUrls(webUrlsText).length} 个有效 URL
                          {parseWebUrls(webUrlsText).length === 0 && (
                            <span className="text-amber-600 ml-1">
                              （所有 URL 格式不正确，需以 http:// 或 https:// 开头）
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="rounded-lg border border-border/60 bg-muted/10 px-3 py-2 text-xs text-muted-foreground leading-relaxed">
                    <p>
                      <strong>限制说明：</strong>仅获取静态 HTML，不支持 JavaScript
                      动态渲染页面（SPA
                      页面可能无法正确提取）；不自动追踪页面内链接，只爬取请求中明确指定的 URL。
                    </p>
                  </div>

                  {(localError || webUploadMutation.isError) && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                      {localError ??
                        (webUploadMutation.error instanceof Error
                          ? webUploadMutation.error.message
                          : '网页摄入失败')}
                    </div>
                  )}

                  {/* --- Auto-rerun interval selector --- */}
                  <div className="flex flex-col gap-1.5 pt-1">
                    <Label className="text-sm font-medium">自动重跑周期</Label>
                    <p className="text-xs text-muted-foreground">
                      设置后，该爬取任务将按设定 URL 周期自动重跑更新知识库
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {[
                        { label: '不自动重跑', hours: 0 },
                        { label: '每 5 分钟', hours: -1, debugOnly: true },
                        { label: '每 1 小时', hours: 1, debugOnly: true },
                        { label: '每 12 小时', hours: 12 },
                        { label: '每天', hours: 24 },
                        { label: '每 3 天', hours: 72 },
                        { label: '每 7 天', hours: 168 },
                      ]
                        .filter((opt) => !('debugOnly' in opt) || isDebug)
                        .map((opt) => (
                          <button
                            key={opt.hours}
                            type="button"
                            className={cn(
                              'inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                              opt.hours === autoRerunIntervalHours
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                            )}
                            disabled={webUploadMutation.isPending}
                            onClick={() => setAutoRerunIntervalHours(opt.hours)}
                          >
                            {opt.label}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setWebUrlsText('');
                        setSitemapUrl('');
                        setSitemapParsedUrls([]);
                        setSitemapUnreachableCount(0);
                        setAutoRerunIntervalHours(0);
                        setLocalError(null);
                      }}
                      disabled={
                        webUploadMutation.isPending ||
                        (webInputMode === 'manual' && webUrlsText.trim().length === 0) ||
                        (webInputMode === 'sitemap' && sitemapParsedUrls.length === 0)
                      }
                    >
                      清空输入
                    </Button>
                    <Button type="submit" disabled={!canSubmit} className="min-w-[140px] gap-2">
                      {webUploadMutation.isPending ? (
                        <>
                          <LoaderIcon className="h-4 w-4 animate-spin" />
                          提交中…
                        </>
                      ) : (
                        '爬取网页'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </form>

            <div id="rag-build-history" className="p-5 space-y-4 border-t border-border/60">
              {jobsQuery.isLoading ? (
                <div className="space-y-3 py-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-lg" />
                  ))}
                </div>
              ) : jobsQuery.isError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border border-destructive/20 bg-destructive/5">
                  <p className="text-sm text-destructive mb-3">加载构建记录失败</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void jobsQuery.refetch()}
                  >
                    重试
                  </Button>
                </div>
              ) : results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg border border-dashed bg-muted/20">
                  <ClipboardIcon className="w-14 h-14 text-muted-foreground/40 mb-3" />
                  <p className="font-medium text-foreground mb-1">暂无构建记录</p>
                  <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                    在上方上传文档并生成后，将在此展示进度与日志（记录已持久化，重启服务仍可查看）。
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-9"
                    onClick={() => {
                      document.getElementById('rag-upload-section')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }}
                  >
                    去上传文档
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground">
                        构建历史
                        <span className="ml-2 font-normal text-muted-foreground">
                          （显示 {filteredGroupedIngestRows.length} / {groupedIngestRows.length}{' '}
                          条）
                        </span>
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        记录来自平台数据库；同一批次上传的多文件对应同一任务 ID。
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                        disabled={
                          buildHistoryRefreshing ||
                          deleteJobsMutation.isPending ||
                          rerunJobsMutation.isPending
                        }
                        onClick={() => void refreshBuildHistory()}
                        aria-label="刷新构建历史"
                        title="重新拉取列表与任务状态"
                      >
                        <RefreshCwIcon
                          className={cn('h-3.5 w-3.5', buildHistoryRefreshing && 'animate-spin')}
                        />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 h-7 px-2 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={
                          !someRowsSelected ||
                          deleteJobsMutation.isPending ||
                          rerunJobsMutation.isPending
                        }
                        onClick={() => {
                          const platformIds = Array.from(selectedRowKeys).flatMap(
                            (k) => groupedIngestRows.find((r) => r.rowKey === k)?.platformIds ?? []
                          );
                          const seen = new Set<string>();
                          const deduped = platformIds.filter((id) => {
                            if (seen.has(id)) return false;
                            seen.add(id);
                            return true;
                          });
                          const ragJobIdsToCloseLogs = Array.from(selectedRowKeys).flatMap((k) => {
                            const r = groupedIngestRows.find((row) => row.rowKey === k);
                            return r?.job_id ? [r.job_id] : [];
                          });
                          setConfirmDelete({
                            platformIds: deduped,
                            ragJobIdsToCloseLogs: [...new Set(ragJobIdsToCloseLogs)],
                            summary: `将先通过 rag 从 Qdrant 删除对应向量，再删除共 ${deduped.length} 条平台数据库记录。已选 ${selectedRowKeys.size} 条展示行。不可恢复。`,
                          });
                        }}
                      >
                        {deleteJobsMutation.isPending ? (
                          <>
                            <LoaderIcon className="h-3 w-3 animate-spin shrink-0" />
                            删除中…
                          </>
                        ) : (
                          '批量删除'
                        )}
                      </Button>
                    </div>
                  </div>
                  {groupedIngestRows.length > 0 && filteredGroupedIngestRows.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center rounded-lg border border-dashed border-border/80 bg-muted/10">
                      无符合当前筛选条件的记录，可调整筛选或
                      <button
                        type="button"
                        className="text-primary underline-offset-2 hover:underline mx-1"
                        onClick={() => {
                          setHistoryCollectionFilter('');
                          setHistoryStatusFilter('');
                        }}
                      >
                        清除筛选
                      </button>
                    </p>
                  ) : (
                    <>
                      {/* Active cron jobs panel */}
                      {activeRerunJobs.length > 0 && (
                        <div className="rounded-lg border border-blue-200/60 bg-blue-50/40 dark:border-blue-800/40 dark:bg-blue-950/20">
                          <div className="px-3 py-2 flex items-center gap-2 border-b border-blue-200/40 dark:border-blue-800/30">
                            <svg
                              className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                            >
                              <path d="M13.65 2.35A8 8 0 1 0 16 8h-2a6 6 0 1 1-1.76-4.24L10 6h6V0l-2.35 2.35z" />
                            </svg>
                            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                              活跃定时任务 ({activeRerunJobs.length})
                            </span>
                            <span className="text-[11px] text-blue-600/80 dark:text-blue-400/80">
                              定时成功后会替换同 Collection 的旧构建记录，下方历史仅保留最新一次
                            </span>
                          </div>
                          <div className="divide-y divide-blue-100/50 dark:divide-blue-900/30">
                            {activeRerunJobs.map((job) => {
                              const collDisplay =
                                job.collection_display_name?.trim() ||
                                collectionDisplayByQdrantId.get(job.collection_id)?.trim() ||
                                job.collection_id ||
                                '—';
                              const urlLines: { text: string; title: string }[] = (() => {
                                if (job.kind === 'sitemap') {
                                  const sitemap = job.sitemap_url?.trim() ?? '';
                                  if (sitemap !== '') {
                                    return [{ text: sitemap, title: sitemap }];
                                  }
                                  return [];
                                }
                                if (job.urls && job.urls.length > 0) {
                                  const urlsTitle = job.urls.join('\n');
                                  const urlsText =
                                    job.urls.length === 1
                                      ? job.urls[0]
                                      : `${job.urls[0]} 等 ${job.urls.length} 个 URL`;
                                  return [{ text: urlsText, title: urlsTitle }];
                                }
                                return [];
                              })();
                              const health = cronExecutionHealth(job);
                              const lastFiredLabel = job.last_fired_at
                                ? formatJobDetailTime(job.last_fired_at)
                                : '尚未执行';
                              const lastFiredRelative = formatRelativeTimeZh(job.last_fired_at);
                              const healthTitle =
                                health === 'ok'
                                  ? '定时任务近期已触发'
                                  : health === 'never'
                                    ? '等待首次定时触发'
                                    : health === 'failed'
                                      ? '最近一次定时执行失败，详见下方红色提示'
                                      : '超过预期间隔仍未触发，请检查后端 cron 调度';
                              const latestJid = job.last_ingest_job_id?.trim() ?? '';
                              const lastRunHint = cronLastRunHint(job);
                              // last_ingest_job_id 一旦写入就不会被清空（只在成功时更新），
                              // 所以「跑了一个月后开始失败」的场景里它依旧指向那次旧的成功任务。
                              // 失败提示必须与它并列渲染，否则最常见的线上形态完全不可见。
                              const lastRunFailed = lastRunHint.tone === 'error';
                              const latestSched = latestJid
                                ? jobScheduleById[latestJid]
                                : undefined;
                              const latestStatus =
                                latestSched?.status && latestSched.status !== ''
                                  ? latestSched.status
                                  : latestJid
                                    ? 'running'
                                    : '';
                              return (
                                <div key={job.id} className="px-3 py-2 space-y-1.5">
                                  <div className="grid grid-cols-[4.5rem_minmax(0,7rem)_minmax(0,1fr)_5.5rem_9rem_9rem_auto] items-center gap-x-3">
                                    <span
                                      className={cn(
                                        'inline-flex w-[4.5rem] items-center justify-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
                                        job.kind === 'sitemap'
                                          ? 'border-violet-300/60 bg-violet-50/80 text-violet-700 dark:border-violet-600/40 dark:bg-violet-900/20 dark:text-violet-300'
                                          : 'border-emerald-300/60 bg-emerald-50/80 text-emerald-700 dark:border-emerald-600/40 dark:bg-emerald-900/20 dark:text-emerald-300'
                                      )}
                                    >
                                      {job.kind === 'sitemap' ? 'Sitemap' : 'Web'}
                                    </span>
                                    <span
                                      className="min-w-0 truncate text-xs text-foreground font-mono"
                                      title={collDisplay}
                                    >
                                      {collDisplay}
                                    </span>
                                    <div className="min-w-0 flex flex-col gap-0.5">
                                      {urlLines.map((line, idx) => (
                                        <span
                                          key={idx}
                                          className="truncate text-[11px] text-muted-foreground"
                                          title={line.title}
                                        >
                                          {line.text}
                                        </span>
                                      ))}
                                    </div>
                                    <span
                                      className="inline-flex w-[5.5rem] shrink-0 items-center justify-center gap-1 rounded-md border border-blue-300/50 bg-blue-100/60 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 dark:border-blue-600/30 dark:bg-blue-900/20 dark:text-blue-300"
                                      title={healthTitle}
                                    >
                                      <span
                                        className={cn(
                                          'h-1.5 w-1.5 shrink-0 rounded-full',
                                          health === 'ok'
                                            ? 'bg-emerald-500'
                                            : health === 'never'
                                              ? 'bg-amber-500'
                                              : 'bg-red-500'
                                        )}
                                      />
                                      {cronToIntervalLabel(job.cron)}
                                    </span>
                                    <span
                                      className="w-[9rem] shrink-0 truncate text-[11px] text-muted-foreground"
                                      title={
                                        job.last_fired_at
                                          ? `${lastFiredLabel}${lastFiredRelative ? `（${lastFiredRelative}）` : ''}`
                                          : '定时任务创建后尚未触发过'
                                      }
                                    >
                                      上次 {lastFiredLabel}
                                      {lastFiredRelative ? (
                                        <span className="ml-1 text-blue-600/70 dark:text-blue-400/70">
                                          ({lastFiredRelative})
                                        </span>
                                      ) : null}
                                    </span>
                                    <span
                                      className="w-[9rem] shrink-0 truncate text-[11px] text-muted-foreground"
                                      title={
                                        job.next_fire_at
                                          ? formatJobDetailTime(job.next_fire_at)
                                          : undefined
                                      }
                                    >
                                      下次{' '}
                                      {job.next_fire_at
                                        ? formatJobDetailTime(job.next_fire_at)
                                        : '—'}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="shrink-0 h-6 rounded-md px-2 text-[11px] font-normal border-blue-400/40 text-blue-600 hover:bg-blue-500/10 hover:text-blue-700"
                                      disabled={deleteRerunJobMutation.isPending}
                                      title="取消此定时重跑"
                                      onClick={() => deleteRerunJobMutation.mutate(job.id)}
                                    >
                                      取消定时
                                    </Button>
                                  </div>
                                  {lastRunFailed ? (
                                    <p
                                      className="pl-[calc(4.5rem+0.75rem)] text-[11px] text-red-600 dark:text-red-400"
                                      title={lastRunHint.detail}
                                    >
                                      {lastRunHint.text}
                                    </p>
                                  ) : null}
                                  {latestJid ? (
                                    <>
                                      <div className="flex flex-wrap items-center gap-2 pl-[calc(4.5rem+0.75rem)]">
                                        <span
                                          className={cn(
                                            'text-[11px] font-medium shrink-0',
                                            lastRunFailed
                                              ? 'text-muted-foreground'
                                              : 'text-blue-700 dark:text-blue-300'
                                          )}
                                          title={
                                            lastRunFailed
                                              ? '最近一次定时执行已失败，这里展示的是此前最后一次成功产出的构建任务'
                                              : undefined
                                          }
                                        >
                                          {lastRunFailed ? '上次成功任务' : '最新任务'}
                                        </span>
                                        <span
                                          className="max-w-[14rem] truncate font-mono text-[11px] text-foreground"
                                          title={latestJid}
                                        >
                                          {latestJid}
                                        </span>
                                        {latestStatus ? (
                                          <JobStatusPill
                                            label={latestStatus}
                                            ok={!statusLabelLooksFailed(latestStatus)}
                                          />
                                        ) : null}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-6 rounded-md px-2 text-[11px] font-normal border-blue-400/40 text-blue-600 hover:bg-blue-500/10"
                                          onClick={() => {
                                            if (
                                              logsExpandedJobId === latestJid &&
                                              logsExpandedSource === 'cron'
                                            ) {
                                              setLogsExpandedJobId(null);
                                              return;
                                            }
                                            setLogsExpandedSource('cron');
                                            setLogsExpandedJobId(latestJid);
                                          }}
                                        >
                                          {logsExpandedJobId === latestJid &&
                                          logsExpandedSource === 'cron'
                                            ? '收起日志'
                                            : '查看日志'}
                                        </Button>
                                      </div>
                                      {logsExpandedJobId === latestJid &&
                                      logsExpandedSource === 'cron' ? (
                                        <div className="pl-[calc(4.5rem+0.75rem)]">
                                          <RagJobLogPanel
                                            fileLabel={streamFileLabel}
                                            streaming={isStreaming && streamJobId === latestJid}
                                            logText={logText}
                                            streamError={streamError}
                                            preRef={logPreRef}
                                          />
                                        </div>
                                      ) : null}
                                    </>
                                  ) : lastRunFailed ? null : (
                                    <p
                                      className="pl-[calc(4.5rem+0.75rem)] text-[11px] text-muted-foreground"
                                      title={lastRunHint.detail}
                                    >
                                      {lastRunHint.text}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div
                        ref={historyTableWrapRef}
                        className="rounded-lg border border-border/80 bg-card/30 shadow-sm min-w-0 w-full [&_th]:px-3 [&_td]:px-3 [&_th]:h-11 [&_td]:py-2.5"
                      >
                        <Table className="table-fixed w-full" suppressHorizontalScroll>
                          <colgroup>
                            {historyTableColPercents.map((p, i) => (
                              <col key={i} style={{ width: `${p}%` }} />
                            ))}
                          </colgroup>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent bg-muted/40 border-border/60">
                              <TableHead
                                className="relative min-w-0 pr-0"
                                style={{
                                  width: `${historyTableColPercents[0]}%`,
                                }}
                              >
                                <input
                                  ref={headerSelectRef}
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-input align-middle"
                                  checked={allSelectableSelected}
                                  disabled={
                                    selectableRows.length === 0 || deleteJobsMutation.isPending
                                  }
                                  onChange={toggleSelectAll}
                                  aria-label="全选"
                                />
                                <HistoryTableColResizeHandle
                                  columnIndex={0}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[1]}%`,
                                }}
                              >
                                任务 ID
                                <HistoryTableColResizeHandle
                                  columnIndex={1}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[2]}%`,
                                }}
                              >
                                <div className="inline-flex items-center gap-1">
                                  <span>Collection</span>
                                  <button
                                    ref={historyFilterCollectionBtnRef}
                                    type="button"
                                    className={cn(
                                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-transparent',
                                      'text-muted-foreground hover:bg-muted hover:text-foreground',
                                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                      historyCollectionFilter !== '' &&
                                        'border-primary/30 bg-primary/10 text-primary'
                                    )}
                                    aria-label="按 Collection 筛选"
                                    aria-expanded={historyFilterMenuOpen === 'collection'}
                                    aria-haspopup="listbox"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoryFilterMenuOpen((m) =>
                                        m === 'collection' ? null : 'collection'
                                      );
                                    }}
                                  >
                                    <FilterFunnelIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <HistoryTableColResizeHandle
                                  columnIndex={2}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 font-medium"
                                style={{
                                  width: `${historyTableColPercents[3]}%`,
                                }}
                              >
                                文档
                                <HistoryTableColResizeHandle
                                  columnIndex={3}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[4]}%`,
                                }}
                              >
                                <div className="inline-flex items-center gap-1">
                                  <span>状态</span>
                                  <button
                                    ref={historyFilterStatusBtnRef}
                                    type="button"
                                    className={cn(
                                      'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-transparent',
                                      'text-muted-foreground hover:bg-muted hover:text-foreground',
                                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                      historyStatusFilter !== '' &&
                                        'border-primary/30 bg-primary/10 text-primary'
                                    )}
                                    aria-label="按状态筛选"
                                    aria-expanded={historyFilterMenuOpen === 'status'}
                                    aria-haspopup="listbox"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHistoryFilterMenuOpen((m) =>
                                        m === 'status' ? null : 'status'
                                      );
                                    }}
                                  >
                                    <FilterFunnelIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <HistoryTableColResizeHandle
                                  columnIndex={4}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[5]}%`,
                                }}
                              >
                                开始时间
                                <HistoryTableColResizeHandle
                                  columnIndex={5}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[6]}%`,
                                }}
                              >
                                结束时间
                                <HistoryTableColResizeHandle
                                  columnIndex={6}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                              <TableHead
                                className="relative min-w-0 text-center whitespace-nowrap font-medium"
                                style={{
                                  width: `${historyTableColPercents[7]}%`,
                                }}
                              >
                                操作
                                <HoverTooltip text="仅 web/sitemap 爬取任务支持重跑，本地上传的文件任务需重新上传">
                                  <span className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-border/60 text-muted-foreground/60 text-[10px] leading-none">
                                    ?
                                  </span>
                                </HoverTooltip>
                                <HistoryTableColResizeHandle
                                  columnIndex={7}
                                  containerRef={historyTableWrapRef}
                                  fracsRef={historyTableColFracsRef}
                                  setFracs={setHistoryTableColFracs}
                                />
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredGroupedIngestRows.map((row) => {
                              const collectionDesc =
                                row.collectionId !== ''
                                  ? (
                                      collectionDescriptionByQdrantId.get(row.collectionId) ?? ''
                                    ).trim()
                                  : '';
                              const jid = row.job_id;
                              const sched = jid ? jobScheduleById[jid] : undefined;
                              const statusLabel = computeBuildHistoryStatusLabel(row, sched);
                              const startedIso = sched?.started_at ?? row.dbCreatedAtMin;
                              const started = formatJobDetailTime(startedIso);
                              const triggerKind = resolveBuildHistoryTrigger(
                                row,
                                activeRerunJobs,
                                startedIso
                              );
                              const finished = (() => {
                                const fin = sched?.finished_at;
                                if (fin != null && String(fin).trim() !== '') {
                                  return formatJobDetailTime(fin);
                                }
                                if (
                                  row.dbCreatedAtMax &&
                                  jobLooksFinished({
                                    status: statusLabel,
                                    finished_at: undefined,
                                  })
                                ) {
                                  return formatJobDetailTime(row.dbCreatedAtMax);
                                }
                                return '—';
                              })();
                              const pollErrDetail = jid
                                ? jobScheduleById[jid]?.errorDetail
                                : undefined;
                              const rerunEligibility = canRerunBuildHistoryRow(row);
                              const rerunPlatformId = row.platformIds[0] ?? '';
                              const rerunNeedsFeishu = isFeishuIngestName(row.name);
                              const rerunBlockedReason = !rerunEligibility.ok
                                ? rerunEligibility.reason
                                : rerunNeedsFeishu && !isFeishuLoggedIn
                                  ? '请先完成飞书授权后再重跑'
                                  : rerunNeedsFeishu && !isFeishuReady
                                    ? '请先配置飞书应用信息后再重跑'
                                    : isNonTerminalBuildStatus(statusLabel)
                                      ? '任务仍在进行中，请稍后再试'
                                      : undefined;
                              return (
                                <Fragment key={row.rowKey}>
                                  <TableRow className="border-border/60">
                                    <TableCell className="w-10 align-middle pr-0">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-input"
                                        checked={selectedRowKeys.has(row.rowKey)}
                                        disabled={
                                          row.platformIds.length === 0 ||
                                          deleteJobsMutation.isPending
                                        }
                                        onChange={() => toggleRowSelected(row.rowKey)}
                                        aria-label="选择该行"
                                      />
                                    </TableCell>
                                    <TableCell className="align-middle overflow-hidden max-w-0">
                                      {jid ? (
                                        <div className="flex min-w-0 items-center gap-1.5">
                                          <span
                                            className="min-w-0 flex-1 truncate whitespace-nowrap font-mono text-xs text-foreground"
                                            title={jid}
                                          >
                                            {jid}
                                          </span>
                                          {triggerKind === 'scheduled' ? (
                                            <BuildHistoryScheduledBadge />
                                          ) : null}
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground text-xs">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="align-middle overflow-hidden max-w-0">
                                      <div className="flex min-w-0 items-center gap-1">
                                        <span
                                          className="min-w-0 flex-1 truncate whitespace-nowrap font-mono text-xs text-foreground"
                                          title={row.collection}
                                        >
                                          {row.collection}
                                        </span>
                                        {collectionDesc ? (
                                          <HoverTooltip text={collectionDesc}>
                                            <NotesHintIcon className="h-3.5 w-3.5" aria-hidden />
                                          </HoverTooltip>
                                        ) : null}
                                      </div>
                                    </TableCell>
                                    <TableCell className="align-middle overflow-hidden min-w-0">
                                      <BuildHistoryDocCell
                                        files={row.files}
                                        name={row.name}
                                        rowKey={row.rowKey}
                                        expanded={docsExpandedRowKeys.has(row.rowKey)}
                                        onToggle={() => toggleDocsRow(row.rowKey)}
                                      />
                                    </TableCell>
                                    <TableCell className="align-middle">
                                      <div className="inline-flex flex-wrap items-center gap-1.5">
                                        <RagJobStatusCell
                                          row={row}
                                          statusLabel={statusLabel}
                                          pollErrorDetail={pollErrDetail}
                                          onOpenFailureReason={setFailureReasonDialog}
                                        />
                                      </div>
                                    </TableCell>
                                    <TableCell className="align-middle text-xs text-foreground tabular-nums whitespace-nowrap">
                                      {started}
                                    </TableCell>
                                    <TableCell className="align-middle text-xs text-foreground tabular-nums whitespace-nowrap">
                                      {finished}
                                    </TableCell>
                                    <TableCell className="text-center align-middle whitespace-nowrap">
                                      <div className="inline-flex flex-wrap justify-center gap-1.5">
                                        {jid ? (
                                          logsExpandedJobId === jid &&
                                          logsExpandedSource === 'history' ? (
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              className={cn(
                                                'h-7 rounded-md px-2 text-[11px] font-normal leading-tight',
                                                'border-primary/40 text-primary hover:bg-primary/8 hover:text-primary',
                                                'bg-primary/10 border-primary/50'
                                              )}
                                              aria-expanded={true}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setLogsExpandedJobId(null);
                                              }}
                                            >
                                              收起
                                            </Button>
                                          ) : (
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              className={cn(
                                                'h-7 rounded-md px-2 text-[11px] font-normal leading-tight',
                                                'border-primary/40 text-primary hover:bg-primary/8 hover:text-primary'
                                              )}
                                              aria-expanded={false}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setLogsExpandedSource('history');
                                                setLogsExpandedJobId(jid);
                                              }}
                                            >
                                              日志
                                            </Button>
                                          )
                                        ) : null}
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className={cn(
                                            'h-7 rounded-md px-2 text-[11px] font-normal leading-tight',
                                            'border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800'
                                          )}
                                          disabled={
                                            rerunPlatformId === '' ||
                                            rerunBlockedReason !== undefined ||
                                            deleteJobsMutation.isPending ||
                                            rerunJobsMutation.isPending
                                          }
                                          title={rerunBlockedReason}
                                          onClick={() =>
                                            setConfirmRerun({
                                              platformId: rerunPlatformId,
                                              ragJobIdToCloseLogs: jid,
                                              summary: buildRerunSummary(row),
                                              needsFeishu: rerunNeedsFeishu,
                                              name: row.name?.trim() || undefined,
                                              qdrantCollectionName:
                                                row.collectionId?.trim() || undefined,
                                            })
                                          }
                                        >
                                          重跑
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className={cn(
                                            'h-7 rounded-md px-2 text-[11px] font-normal leading-tight',
                                            'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive'
                                          )}
                                          disabled={
                                            row.platformIds.length === 0 ||
                                            deleteJobsMutation.isPending ||
                                            rerunJobsMutation.isPending
                                          }
                                          onClick={() =>
                                            setConfirmDelete({
                                              platformIds: row.platformIds,
                                              ragJobIdsToCloseLogs: jid ? [jid] : [],
                                              summary: jid
                                                ? `将先通过 rag 从 Qdrant 删除本任务涉及的向量，再删除平台中的 ${row.platformIds.length} 条持久化记录（多文件同批次会一并删除）。不可恢复。`
                                                : `将先尝试清理 Qdrant 中的对应向量（若有），再删除该条对应的 ${row.platformIds.length} 条数据库行。不可恢复。`,
                                            })
                                          }
                                        >
                                          删除
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                  {jid &&
                                    logsExpandedJobId === jid &&
                                    logsExpandedSource === 'history' && (
                                      <TableRow>
                                        <TableCell colSpan={8} className="p-0">
                                          <RagJobLogPanel
                                            fileLabel={streamFileLabel}
                                            streaming={isStreaming && streamJobId === jid}
                                            logText={logText}
                                            streamError={streamError}
                                            preRef={logPreRef}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    )}
                                </Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}

                  {historyFilterMenuOpen &&
                    historyFilterDropdownPos &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <div
                        ref={historyFilterDropdownRef}
                        className="fixed z-[100] rounded-md border border-border bg-background p-2 shadow-lg"
                        style={{
                          top: historyFilterDropdownPos.top,
                          left: historyFilterDropdownPos.left,
                          minWidth: historyFilterDropdownPos.width,
                        }}
                      >
                        {historyFilterMenuOpen === 'collection' ? (
                          <>
                            <select
                              value={historyCollectionFilter}
                              onChange={(e) => setHistoryCollectionFilter(e.target.value)}
                              aria-label="Collection"
                              className="h-9 w-full min-w-[10rem] rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <option value="">全部</option>
                              {historyCollectionOptions.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                            <div className="mt-2 flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                  setHistoryCollectionFilter('');
                                  closeHistoryFilterMenu();
                                }}
                              >
                                重置
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <select
                              value={historyStatusFilter}
                              onChange={(e) => setHistoryStatusFilter(e.target.value)}
                              aria-label="状态"
                              className="h-9 w-full min-w-[10rem] rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <option value="">全部</option>
                              {historyStatusOptions.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                            <div className="mt-2 flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                  setHistoryStatusFilter('');
                                  closeHistoryFilterMenu();
                                }}
                              >
                                重置
                              </Button>
                            </div>
                          </>
                        )}
                      </div>,
                      document.body
                    )}

                  <Dialog
                    open={filePreviewTarget !== null}
                    onOpenChange={(open) => {
                      if (!open) closeFilePreview();
                    }}
                  >
                    <DialogContent className="max-w-3xl">
                      <DialogHeader>
                        <DialogTitle className="truncate pr-8">
                          {filePreviewTarget?.name ?? '文件预览'}
                        </DialogTitle>
                        <DialogDescription>
                          {filePreviewLoading
                            ? '正在加载预览…'
                            : (filePreviewResult?.subtitle ?? filePreviewError ?? '第一页预览')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="max-h-[min(70vh,640px)] overflow-auto rounded-md border border-border/80 bg-muted/20">
                        {filePreviewLoading ? (
                          <div className="flex items-center justify-center gap-2 px-4 py-16 text-sm text-muted-foreground">
                            <LoaderIcon className="h-4 w-4 animate-spin" />
                            正在生成预览…
                          </div>
                        ) : filePreviewError ? (
                          <p className="px-4 py-8 text-sm text-destructive">{filePreviewError}</p>
                        ) : filePreviewResult?.kind === 'image' ? (
                          <img
                            src={filePreviewResult.dataUrl}
                            alt={`${filePreviewTarget?.name ?? '文件'} 第一页预览`}
                            className="mx-auto block max-w-full h-auto"
                          />
                        ) : filePreviewResult?.kind === 'text' ? (
                          <pre className="whitespace-pre-wrap break-words px-4 py-3 text-sm text-foreground">
                            {filePreviewResult.content}
                          </pre>
                        ) : null}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <AlertDialog
                    open={failureReasonDialog !== null}
                    onOpenChange={(open) => {
                      if (!open) setFailureReasonDialog(null);
                    }}
                  >
                    <AlertDialogContent className="max-w-lg">
                      <AlertDialogHeader>
                        <AlertDialogTitle>失败原因</AlertDialogTitle>
                      </AlertDialogHeader>
                      <div className="max-h-[min(55vh,380px)] overflow-y-auto rounded-md border border-border/80 bg-muted/30 px-3 py-2.5 text-sm text-foreground whitespace-pre-wrap break-words">
                        {failureReasonDialog}
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogAction onClick={() => setFailureReasonDialog(null)}>
                          知道了
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog
                    open={confirmRerun !== null}
                    onOpenChange={(open) => {
                      if (!open) {
                        setConfirmRerun(null);
                        rerunJobsMutation.reset();
                      }
                    }}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>确认重跑构建任务？</AlertDialogTitle>
                        <AlertDialogDescription>{confirmRerun?.summary}</AlertDialogDescription>
                      </AlertDialogHeader>
                      {rerunJobsMutation.isError ? (
                        <p className="text-sm text-destructive">
                          {rerunJobsMutation.error instanceof Error
                            ? rerunJobsMutation.error.message
                            : '重跑失败'}
                        </p>
                      ) : null}
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setConfirmRerun(null)}>
                          取消
                        </AlertDialogCancel>
                        <AlertDialogAction
                          disabled={
                            rerunJobsMutation.isPending ||
                            !confirmRerun ||
                            confirmRerun.platformId === ''
                          }
                          onClick={() => {
                            if (!confirmRerun || confirmRerun.platformId === '') return;
                            rerunJobsMutation.mutate({
                              platformId: confirmRerun.platformId,
                              needsFeishu: confirmRerun.needsFeishu,
                              ragJobIdToCloseLogs: confirmRerun.ragJobIdToCloseLogs,
                              name: confirmRerun.name,
                              qdrantCollectionName: confirmRerun.qdrantCollectionName,
                            });
                          }}
                        >
                          {rerunJobsMutation.isPending ? (
                            <span className="inline-flex items-center">
                              <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                              重跑中…
                            </span>
                          ) : (
                            '重跑'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog
                    open={confirmDelete !== null}
                    onOpenChange={(open) => {
                      if (!open) {
                        setConfirmDelete(null);
                        deleteJobsMutation.reset();
                      }
                    }}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>确认删除构建记录？</AlertDialogTitle>
                        <AlertDialogDescription>{confirmDelete?.summary}</AlertDialogDescription>
                      </AlertDialogHeader>
                      {deleteJobsMutation.isError ? (
                        <p className="text-sm text-destructive">
                          {deleteJobsMutation.error instanceof Error
                            ? deleteJobsMutation.error.message
                            : '删除失败'}
                        </p>
                      ) : null}
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setConfirmDelete(null)}>
                          取消
                        </AlertDialogCancel>
                        <AlertDialogAction
                          disabled={
                            deleteJobsMutation.isPending ||
                            !confirmDelete ||
                            confirmDelete.platformIds.length === 0
                          }
                          className="bg-destructive hover:bg-destructive/90"
                          onClick={() => {
                            if (!confirmDelete || confirmDelete.platformIds.length === 0) return;
                            deleteJobsMutation.mutate({
                              platformIds: confirmDelete.platformIds,
                              ragJobIdsToCloseLogs: confirmDelete.ragJobIdsToCloseLogs,
                            });
                          }}
                        >
                          {deleteJobsMutation.isPending ? (
                            <span className="inline-flex items-center">
                              <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                              删除中…
                            </span>
                          ) : (
                            '删除'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        </div>

        <AlertDialog
          open={batchDeleteCollectionsDialog !== null && batchDeleteCollectionsDialog.length > 0}
          onOpenChange={(open) => {
            if (!open) {
              setBatchDeleteCollectionsDialog(null);
              deleteCollectionsBatchMutation.reset();
            }
          }}
        >
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>
                确认删除选中的 {batchDeleteCollectionsDialog?.length ?? 0} 个 Collection？
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                将依次通过 rag 删除 Qdrant 中的向量集合，并删除平台数据库中的{' '}
                <span className="font-mono text-xs">rags</span> 登记。
              </p>
              <ul className="list-disc pl-4 space-y-0.5 max-h-36 overflow-y-auto text-foreground text-xs">
                {(batchDeleteCollectionsDialog ?? []).slice(0, 12).map((r) => (
                  <li key={r.id}>{r.collection_display_name?.trim() || r.collection_name}</li>
                ))}
              </ul>
              {(batchDeleteCollectionsDialog?.length ?? 0) > 12 ? (
                <p className="text-xs">… 等共 {batchDeleteCollectionsDialog?.length} 项</p>
              ) : null}
              <p className="text-destructive font-medium">此操作不可恢复。</p>
              <p className="text-xs">
                构建历史中的旧任务记录不会自动删除；可在本页下方「构建历史」中筛选后批量删除。
              </p>
            </div>
            {deleteCollectionsBatchMutation.isError ? (
              <p className="text-sm text-destructive">
                {formatRagApiError(deleteCollectionsBatchMutation.error)}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setBatchDeleteCollectionsDialog(null);
                  deleteCollectionsBatchMutation.reset();
                }}
              >
                取消
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={
                  deleteCollectionsBatchMutation.isPending ||
                  !batchDeleteCollectionsDialog ||
                  batchDeleteCollectionsDialog.length === 0
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (!batchDeleteCollectionsDialog?.length) return;
                  deleteCollectionsBatchMutation.mutate(
                    batchDeleteCollectionsDialog.map((r) => r.id)
                  );
                }}
              >
                {deleteCollectionsBatchMutation.isPending ? (
                  <span className="inline-flex items-center">
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                    删除中…
                  </span>
                ) : (
                  '确认删除'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
